import { useMusicBeatGrid } from "../lib/musicBeatGrid";
import { timeChoreography } from "../lib/beatSync";
import { needsStoryFrame, renditionUsesVideo } from "../lib/renditionMediaFormat";
import { PostFormatToggle } from "./PostFormatToggle";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import {
  ChevronLeft,
  ChevronRight,
  GripVertical,
  Keyboard,
  Images,
  Loader2,
  MessageCircle,
  Music,
  Play,
  PlusCircle,
  Redo2,
  Share2,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Undo2,
  Video,
  X,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import posthog from "posthog-js";
import { cn, isImageOverlay, isSvgOverlay, isTextOverlay } from "../lib/utils";
import {
  WATERMARK_STORAGE_PATH,
  WATERMARK_PUBLIC_URL,
  WATERMARK_POSITION,
  WATERMARK_WIDTH_PCT,
} from "../constants/watermark";
import { PlatformType } from "../models/Platform";
import { PlatformPostFrame } from "./PlatformPostFrame";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import { useUpgradePage } from "../hooks/useUpgradePage";
import {
  AnyOverlay,
  ExtractedArticleImage,
  ImageOverlay,
  MediaItem,
  SvgOverlay,
  TextOverlay,
  VideoSettingsOverrides,
} from "../models/Post";
import { BrandAsset, BrandKit } from "../models/BrandKit";
import { getBrandKitsForUser } from "../lib/brandKitUtils";
import { resolveMusicUrl } from "../lib/musicUrl";
import { db, getSignedUrl } from "../services/Firebase";
import {
  PexelsImage,
  PexelsImageDownloadError,
  PexelsService,
} from "../services/PexelsService";
import {
  createDefaultTextOverlay,
  generateUniqueId,
  getNextZIndex,
  syncZIndexToArrayOrder,
} from "../utils/textOverlayUtils";
import { applyOverlayImageReplacement } from "../utils/overlayImageReplace";
import { BackgroundFormatControls } from "./BackgroundFormatControls";
import {
  getBrandColorShortcuts,
  type BrandKitColorPickerContext,
} from "./BrandColorShortcuts";
import { ImageLibraryPanel } from "./ImageLibraryPanel";
import { ImageOverlayFormatControls } from "./ImageOverlayFormatControls";
import { ImageSourceButton } from "./ImageSourceButton";
import { VideoAudioSettings } from "./VideoAudioSettings";
import { prefetchMusicLibrary } from "./MusicPicker";
import { ScenePreviewCanvasWrapper, EditorMode } from "./ScenePreviewCanvasWrapper";
import { CarouselVideoPreview } from "../remotion/CarouselVideoPreview";
import { StoryFramePreview } from "../remotion/StoryFramePreview";
import { SlideData } from "../remotion/compositions/CarouselVideo";
import { VideoChoreography } from "../models/ChoreographySpec";
import { SHAPE_LIBRARY, ShapeDefinition, ShapeLibraryView } from "./ShapeLibraryView";
import { SvgOverlayFormatControls } from "./SvgOverlayFormatControls";
import { TextOverlayFormatControls } from "./TextOverlayFormatControls";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useToast } from "./ui/use-toast";
import { namespaceSvgIds } from "../lib/svgIds";

// Overlay width/height are stored as percentages of the canvas, where width%
// is relative to canvas width and height% is relative to canvas height. On a
// non-square canvas (e.g. 1080×1350) those scales differ, so equal percents
// produce a rectangle. This computes a height% that preserves the image's
// pixel aspect ratio for the given canvas.
function computeOverlaySizeForImage(
  imgWidth: number | undefined,
  imgHeight: number | undefined,
  canvasWidth: number | undefined,
  canvasHeight: number | undefined,
  defaultWidthPercent: number,
): { width: number; height: number } {
  if (!imgWidth || !imgHeight || !canvasWidth || !canvasHeight) {
    return { width: defaultWidthPercent, height: defaultWidthPercent };
  }
  const imgAspect = imgWidth / imgHeight;
  const canvasAspect = canvasWidth / canvasHeight;
  const heightPercent = (defaultWidthPercent * canvasAspect) / imgAspect;
  return { width: defaultWidthPercent, height: heightPercent };
}

interface ImageCompositeEditorProps {
  mediaItems: MediaItem[];
  selectedItemPreviewUrl?: string;
  selectedOverlayPreviewUrls?: Record<string, string>;
  selectedItemIndex: number;
  onSelectItem: (index: number) => void;
  overlays: AnyOverlay[];
  onOverlaysChange: (newOverlays: AnyOverlay[]) => void;
  onBackgroundChange: (
    path: string,
    metadata: Pick<MediaItem["metadata"], "dimensions" | "size">
  ) => void;
  backgroundColor?: string;
  onBackgroundColorChange?: (
    backgroundColor: string,
    path: string,
    metadata: Pick<MediaItem["metadata"], "dimensions" | "size">
  ) => void;

  userId: string;
  draftId?: string;
  extractedArticleImages?: ExtractedArticleImage[]; // Images from source article
  bottomSlot?: React.ReactNode;
  onBackgroundImageResolved?: (url: string) => void;
  isBackgroundTemplateAsset?: boolean;
  onBackgroundTemplateAssetChange?: (value: boolean) => void;
  backgroundFilter?: import("../models/Post").ImageFilter;
  onBackgroundFilterChange?: (value: import("../models/Post").ImageFilter | undefined) => void;
  activeBrandKitId?: string | null;

  // Undo/redo
  saveSnapshot?: () => void;
  undo?: () => void;
  redo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;

  // Video preview
  savedVideoChoreography?: VideoChoreography;
  savedVideoChoreographyHash?: string;
  savedVideoMusicPath?: string;
  savedVideoAudioMuted?: boolean;
  savedVideoSpeed?: number;
  onVideoChoreographyChange?: (choreography: VideoChoreography | null) => void;
  /** Music the server picked and saved on convert or regenerate. */
  onVideoMusicResolved?: (musicPath: string) => void;
  onPersistVideoSettings?: (
    fields: Record<string, unknown>
  ) => Promise<void>;
  onBeforeVideoRevert?: () => Promise<void>;
  onVideoReverted?: () => void;
  onVideoMutationPendingChange?: (pending: boolean) => void;
  videoSettingsDisabled?: boolean;
  onBeforeVideoConvert?: () => Promise<void>;

  // Per-channel rendition preview (drill-down on a connection): shows how
  // this post publishes on that platform. format "video" renders the video
  // rendition in the video stage (choreography fetched with target=rendition
  // — the post stays a carousel) with the same Video Settings right panel;
  // format "carousel" shows a read-only slide preview with no right bar.
  // Slide editing is disabled in both — the drill-down scope is
  // rendition-only settings.
  renditionPreview?: {
    platform: PlatformType;
    format: "video" | "carousel" | "story";
    title?: string;
    channelName?: string;
    profileImage?: string | null;
    // Effective caption for this platform (per-connection override or global)
    caption?: string;
    // This connection's video overrides (settings.X ?? post.X). Sidebar
    // changes in drill-down write here, never to the post's defaults.
    videoOverrides?: VideoSettingsOverrides;
    onVideoOverridesChange?: (patch: Partial<VideoSettingsOverrides>) => void;
  };
}

const ACTIVE_BRAND_KIT_STORAGE_PREFIX =
  "wavegen:create-post:active-brand-kit";

function getStoredActiveBrandKitId(userId: string | undefined): string | null {
  if (!userId || typeof window === "undefined") return null;

  try {
    return window.localStorage.getItem(
      `${ACTIVE_BRAND_KIT_STORAGE_PREFIX}:${userId}`
    );
  } catch {
    return null;
  }
}

function storeActiveBrandKitId(
  userId: string | undefined,
  brandKitId: string
) {
  if (!userId || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      `${ACTIVE_BRAND_KIT_STORAGE_PREFIX}:${userId}`,
      brandKitId
    );
  } catch {
    // Non-critical: private browsing/storage restrictions should not block editing.
  }
}

export function ImageCompositeEditor({
  mediaItems,
  selectedItemPreviewUrl,
  selectedOverlayPreviewUrls,
  selectedItemIndex,
  onSelectItem,
  overlays: initialOverlays,
  onOverlaysChange,
  bottomSlot,
  onBackgroundChange,
  backgroundColor: initialBackgroundColor,
  onBackgroundColorChange,
  userId,
  draftId,
  extractedArticleImages,
  onBackgroundImageResolved,
  isBackgroundTemplateAsset,
  onBackgroundTemplateAssetChange,
  backgroundFilter,
  onBackgroundFilterChange,
  activeBrandKitId,
  saveSnapshot,
  undo,
  redo,
  canUndo,
  canRedo,
  savedVideoChoreography,
  savedVideoChoreographyHash,
  savedVideoMusicPath,
  savedVideoAudioMuted,
  savedVideoSpeed,
  onVideoChoreographyChange,
  onVideoMusicResolved,
  onPersistVideoSettings,
  onBeforeVideoRevert,
  onVideoReverted,
  onVideoMutationPendingChange,
  videoSettingsDisabled = false,
  onBeforeVideoConvert,
  renditionPreview,
}: ImageCompositeEditorProps) {
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const storage = useMemo(() => getStorage(db.app), []);

  // --- Video Post Mode ---
  const [isVideoPost, setIsVideoPost] = useState(!!savedVideoChoreography);
  const [videoChoreography, setVideoChoreography] = useState<VideoChoreography | null>(
    savedVideoChoreography ?? null
  );
  const [isLoadingChoreography, setIsLoadingChoreography] = useState(false);
  const [videoMusicPath, setVideoMusicPath] = useState<string | undefined>(savedVideoMusicPath);
  const [videoMusicUrl, setVideoMusicUrl] = useState<string | undefined>();
  const [videoAudioMuted, setVideoAudioMuted] = useState(!!savedVideoAudioMuted);
  const [videoSpeed, setVideoSpeed] = useState(savedVideoSpeed ?? 1);
  const [isRevertingToCarousel, setIsRevertingToCarousel] = useState(false);
  const [videoEditingSlide, setVideoEditingSlide] = useState(false); // true = editing slide, false = playing video
  const prevSelectedIndexRef = useRef(selectedItemIndex);

  // --- Per-channel rendition preview ---
  // Separate state from videoChoreography so the rendition never trips the
  // convert/revert machinery: the post stays a carousel.
  // Applies to video posts too for platforms that support video output, so
  // drill-down can show the video stage without a rendition fetch.
  const inRenditionPreview = !!renditionPreview;
  const inVideoRenditionPreview =
    !!renditionPreview && renditionUsesVideo(
      renditionPreview.format, isVideoPost, renditionPreview.videoOverrides?.videoEnabled
    );
  // Story is a presentation style; its content can be either images or video.
  const inCarouselRenditionPreview = inRenditionPreview && !inVideoRenditionPreview;
  // Image stories publish 9:16 frames laid out like the video rendition, so
  // their preview needs the same resolved slides as the video stage.
  const showStoryFrames =
    inCarouselRenditionPreview &&
    renditionPreview?.format === "story" &&
    mediaItems.some(needsStoryFrame);
  // TikTok photos and IG/FB stories sit in a 9:16 phone viewport
  const usePhoneSlide =
    renditionPreview?.platform === "tiktok" ||
    renditionPreview?.format === "story";
  // Facebook has no organic carousel — multi-image posts render as a collage
  const useFbCollage =
    renditionPreview?.platform === "facebook" &&
    renditionPreview?.format === "carousel" &&
    mediaItems.length > 1;

  // Published output carries the watermark for free plans, so drill-down
  // previews show it (the editing canvas stays clean). Same overlay
  // PostViewer / RemotionOverlayRenderer inject client-side.
  const { isFreePlan } = useStripe();
  const { navigateToUpgrade } = useUpgradePage();

  // The preview is showing the watermark — the most persuasive moment to
  // offer removing it
  const showWatermarkUpsell =
    inRenditionPreview &&
    isFreePlan &&
    mediaItems.some((m) => m?.metadata?.generatedWithWatermark);
  const withPreviewWatermark = useCallback(
    (overlays: AnyOverlay[], item?: MediaItem): AnyOverlay[] => {
      if (
        !inRenditionPreview ||
        !isFreePlan ||
        !item?.metadata?.generatedWithWatermark
      ) {
        return overlays;
      }
      // The SAME PNG the server composites on publish (see
      // constants/watermark.ts) — preview matches output pixel-for-pixel
      return [
        ...overlays,
        {
          id: "__watermark__",
          type: "image" as const,
          filePath: WATERMARK_STORAGE_PATH,
          position: WATERMARK_POSITION,
          width: WATERMARK_WIDTH_PCT,
          opacity: 100,
          zIndex: 9999,
          objectFit: "contain" as const,
        } as AnyOverlay,
      ];
    },
    [inRenditionPreview, isFreePlan]
  );
  const [renditionChoreography, setRenditionChoreography] =
    useState<VideoChoreography | null>(null);
  const [isLoadingRendition, setIsLoadingRendition] = useState(false);
  const [renditionError, setRenditionError] = useState<string | null>(null);
  const [renditionRetryToken, setRenditionRetryToken] = useState(0);
  // Drill-down video settings flyout (anchored beside the preview frame)
  const [videoSettingsOpen, setVideoSettingsOpen] = useState(false);
  useEffect(() => {
    setVideoSettingsOpen(false);
  }, [renditionPreview?.platform, renditionPreview?.format]);
  // The video stage (player + Video Settings panel) serves both modes.
  // Uploaded video files never reach this editor — CreatePost renders them
  // on its own video branch (see UploadedVideoRenditionPreview).
  const showVideoStage = inRenditionPreview ? inVideoRenditionPreview : isVideoPost;

  // Close any in-progress slide editing when entering a drill-down —
  // rendition previews never expose an editing surface
  useEffect(() => {
    if (inRenditionPreview) setVideoEditingSlide(false);
  }, [inRenditionPreview]);

  useEffect(() => {
    // Video posts use their own choreography — nothing to fetch
    if (!inVideoRenditionPreview || isVideoPost || !draftId || !currentUser)
      return;
    let cancelled = false;
    setIsLoadingRendition(true);
    setRenditionError(null);
    (async () => {
      try {
        // target=rendition: idempotent while slides are unchanged, never
        // converts the post (videoEnabled stays false)
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/carousel-video/choreography`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ postId: draftId, target: "rendition" }),
          }
        );
        const data = await response.json();
        if (!response.ok || !data.success || !data.choreography) {
          throw new Error(data.error || "Failed to generate video preview");
        }
        if (cancelled) return;
        setRenditionChoreography(data.choreography);
        // Auto-selected music lands on the post; adopt it locally unless the
        // user already has a track
        if (data.music?.musicPath) {
          setVideoMusicPath((prev) => prev ?? data.music.musicPath);
        }
      } catch (error: any) {
        console.error("Rendition preview failed:", error);
        if (!cancelled) {
          setRenditionError(error.message || "Failed to generate video preview");
        }
      } finally {
        if (!cancelled) setIsLoadingRendition(false);
      }
    })();
    return () => {
      cancelled = true;
      // Always clear the flag: leaving the drill-down mid-fetch must not
      // strand the video stage (shared with convert-to-video) on a spinner
      setIsLoadingRendition(false);
    };
  }, [inVideoRenditionPreview, isVideoPost, draftId, currentUser, renditionRetryToken]);

  // When a slide is selected in video mode (index changes), enter editing.
  // Skip during loading (initial conversion).
  useEffect(() => {
    if (isVideoPost && !renditionPreview && !isLoadingChoreography && selectedItemIndex !== prevSelectedIndexRef.current) {
      setVideoEditingSlide(true);
    }
    prevSelectedIndexRef.current = selectedItemIndex;
  }, [isVideoPost, renditionPreview, isLoadingChoreography, selectedItemIndex]);

  // Handle filmstrip click in video mode — including clicking the already-selected slide
  const handleFilmstripClickInVideoMode = useCallback(() => {
    if (isVideoPost && !renditionPreview && !isLoadingChoreography && !videoEditingSlide) {
      setVideoEditingSlide(true);
    }
  }, [isVideoPost, renditionPreview, isLoadingChoreography, videoEditingSlide]);

  // Effective video values for the active view: in drill-down, this
  // connection's overrides layer over the post defaults (settings.X ?? post.X
  // — mirroring how publish resolves them); otherwise the post defaults.
  const videoOverrides = inRenditionPreview
    ? renditionPreview?.videoOverrides
    : undefined;
  const effectiveMusicPath = videoOverrides?.videoMusicPath ?? videoMusicPath;
  const effectiveAudioMuted = videoOverrides?.videoAudioMuted ?? videoAudioMuted;
  const effectiveSpeed = videoOverrides?.videoSpeed ?? videoSpeed;

  // Resolve music storage path to URL
  useEffect(() => {
    if (effectiveAudioMuted || !effectiveMusicPath) {
      setVideoMusicUrl(undefined);
      return;
    }
    let cancelled = false;
    setVideoMusicUrl(undefined);
    resolveMusicUrl(effectiveMusicPath, currentUser).then((url) => {
      if (!cancelled) setVideoMusicUrl(url);
    }).catch((error) => {
      console.warn("Failed to resolve video music URL:", error);
      if (!cancelled) setVideoMusicUrl(undefined);
    });
    return () => { cancelled = true; };
  }, [currentUser, effectiveAudioMuted, effectiveMusicPath]);

  // Apply speed to choreography for preview (scales animation durations, keeps audio normal)
  const activeChoreography = isVideoPost
    ? videoChoreography
    : inVideoRenditionPreview
      ? renditionChoreography
      : null;
  // Plays as published: scaled by speed, then cut on the music's beat
  const beatGrid = useMusicBeatGrid(effectiveMusicPath, effectiveAudioMuted, currentUser);
  const speedAdjustedChoreography = useMemo(
    () => (activeChoreography ? timeChoreography(activeChoreography, effectiveSpeed, beatGrid) : activeChoreography),
    [activeChoreography, effectiveSpeed, beatGrid]
  );

  const videoPreviewSlides: SlideData[] = useMemo(() => {
    if (!showVideoStage && !showStoryFrames) return [];
    return mediaItems.map((item) => ({
      overlays: withPreviewWatermark(item.metadata?.overlays ?? [], item),
      backgroundImageUrl: undefined, // resolved below per-item
      backgroundColor: item.metadata?.backgroundColor,
      backgroundFilter: item.metadata?.backgroundFilter,
      dimensions: item.metadata?.dimensions ?? { width: 1080, height: 1350 },
      imageUrls: {},
      isBackgroundTemplateAsset: item.metadata?.isBackgroundTemplateAsset,
    }));
  }, [showVideoStage, showStoryFrames, mediaItems, withPreviewWatermark]);

  // Resolve background URLs for all slides when video preview is active
  const [resolvedVideoSlides, setResolvedVideoSlides] = useState<SlideData[]>([]);
  useEffect(() => {
    if ((!showVideoStage && !showStoryFrames) || videoPreviewSlides.length === 0) {
      setResolvedVideoSlides([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const resolved = await Promise.all(
        mediaItems.map(async (item, i) => {
          let bgUrl: string | undefined;
          try {
            if (item.path) bgUrl = await getSignedUrl(item.path);
          } catch { /* ignore */ }
          // Resolve overlay image URLs
          const imageUrls: Record<string, string> = {};
          for (const overlay of item.metadata?.overlays ?? []) {
            if (overlay.type === "image" && (overlay as any).filePath) {
              try {
                imageUrls[(overlay as any).filePath] = await getSignedUrl((overlay as any).filePath);
              } catch { /* ignore */ }
            }
          }
          // Watermark PNG resolves via its public URL (it isn't in the
          // slide's own overlays, so the loop above never maps it)
          imageUrls[WATERMARK_STORAGE_PATH] = WATERMARK_PUBLIC_URL;
          return { ...videoPreviewSlides[i], backgroundImageUrl: bgUrl, imageUrls };
        })
      );
      if (!cancelled) setResolvedVideoSlides(resolved);
    })();
    return () => { cancelled = true; };
  }, [showVideoStage, showStoryFrames, videoPreviewSlides, mediaItems]);

  const handleConvertToVideo = async (force = false) => {
    if (mediaItems.length < 1) return;
    const previousVideoMode = isVideoPost;
    setIsVideoPost(true);
    setVideoEditingSlide(false);
    setSelectedOverlayId(null);

    if (!draftId || !currentUser) return;

    void prefetchMusicLibrary(currentUser).catch(() => {});
    setIsLoadingChoreography(true);
    onVideoMutationPendingChange?.(true);
    try {
      // The endpoint reads the post from Firestore. Flush slide edits first so
      // choreography is generated from what is currently on canvas.
      await onBeforeVideoConvert?.();
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/carousel-video/choreography`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ postId: draftId, force }),
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success || !data.choreography) {
        throw new Error(data.error || "Failed to convert post to video");
      }
      setVideoChoreography(data.choreography);
      setChoreographyHash(data.choreographyHash ?? computeStructuralHash());
      if (data.music?.musicPath) {
        setVideoMusicPath(data.music.musicPath);
        onVideoMusicResolved?.(data.music.musicPath);
      }
      onVideoChoreographyChange?.(data.choreography);
    } catch (error) {
      console.error("Failed to generate choreography:", error);
      setIsVideoPost(previousVideoMode);
      toast({
        variant: "destructive",
        title: "Couldn’t convert this post",
        description: "Your edits are safe. Please try converting again.",
      });
    } finally {
      setIsLoadingChoreography(false);
      onVideoMutationPendingChange?.(false);
    }
  };

  const handleRevertToCarousel = async () => {
    if (!draftId) return;

    setIsRevertingToCarousel(true);
    onVideoMutationPendingChange?.(true);
    try {
      await onBeforeVideoRevert?.();
      const { doc, updateDoc } = await import("firebase/firestore");
      await updateDoc(doc(db, "posts", draftId), {
        videoEnabled: false,
        updatedAt: Date.now(),
      });

      setIsVideoPost(false);
      onVideoChoreographyChange?.(null);
      onVideoReverted?.();
    } catch (error) {
      console.error("Failed to revert video to carousel:", error);
      toast({
        variant: "destructive",
        title: "Couldn't switch format",
        description: "Your format hasn't changed. Please try again.",
      });
    } finally {
      setIsRevertingToCarousel(false);
      onVideoMutationPendingChange?.(false);
    }
  };

  // Structural hash for choreography invalidation detection
  // Only track slide count + overlay IDs — background path/color changes don't affect choreography
  const computeStructuralHash = useCallback(() => {
    return mediaItems
      .map((item) => {
        const overlayIds = (item.metadata?.overlays ?? [])
          .map((o) => o.id)
          .sort()
          .join(',');
        return overlayIds;
      })
      .join('|');
  }, [mediaItems]);

  // Track the hash that was saved with the current choreography (from Firestore or API response)
  // For pre-existing posts without a persisted hash, use current state as baseline
  const [choreographyHash, setChoreographyHash] = useState<string>(() =>
    savedVideoChoreographyHash ?? (savedVideoChoreography
      ? mediaItems.map((item) =>
          (item.metadata?.overlays ?? []).map((o) => o.id).sort().join(',')
        ).join('|')
      : '')
  );

  // Show regenerate banner when current structure doesn't match the hash saved with choreography
  const showInvalidationBanner = useMemo(() => {
    if (!isVideoPost || !videoChoreography || !choreographyHash) return false;
    return computeStructuralHash() !== choreographyHash;
  }, [isVideoPost, videoChoreography, choreographyHash, computeStructuralHash]);

  const onOverlaysChangeRef = useRef(onOverlaysChange);
  const initialOverlaysRef = useRef(initialOverlays);

  useEffect(() => {
    onOverlaysChangeRef.current = onOverlaysChange;
    initialOverlaysRef.current = initialOverlays;
  });

  useEffect(() => {
    console.log("[ImageCompositeEditor] mount");
    return () => {
      console.log("[ImageCompositeEditor] unmount");
    };
  }, []);

  const handleSetOverlays = useCallback(
    (
      updater: AnyOverlay[] | ((prevOverlays: AnyOverlay[]) => AnyOverlay[])
    ) => {
      const newOverlays =
        typeof updater === "function"
          ? updater(initialOverlaysRef.current)
          : updater;
      console.log("[ImageCompositeEditor] handleSetOverlays", {
        updateType: typeof updater === "function" ? "function" : "direct",
        newOverlayIds: Array.isArray(newOverlays)
          ? newOverlays.map((overlay) => overlay.id)
          : null,
      });
      onOverlaysChangeRef.current(newOverlays);
    },
    []
  );

  const [isUploadingBackground, setIsUploadingBackground] = useState(false);
  const [isUploadingOverlay, setIsUploadingOverlay] = useState(false);
  const [naturalImageDimensions, setNaturalImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState<
    string | null
  >(null);
  const [isLoadingBackgroundPreview, setIsLoadingBackgroundPreview] =
    useState(false);
  // A child format-controls panel drilled into a sub-level (e.g. a text
  // overlay's Edit Highlight); the shared header shows its title and the
  // back button pops that level first
  const [overlayDrillDown, setOverlayDrillDown] = useState<{
    title: string;
    onBack: () => void;
  } | null>(null);

  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(
    null
  );
  const [isBackgroundSelected, setIsBackgroundSelected] = useState(false);
  // Unified image library panel mode (replaces separate searchMode / articlePickerMode / brandKitPickerMode)
  const [libraryMode, setLibraryMode] = useState<
    "background" | "overlay" | "overlay-change" | null
  >(null);
  const [editorMode, setEditorMode] = useState<EditorMode>("remotion");
  const [shapeLibraryMode, setShapeLibraryMode] = useState(false);
  const [overlayIdToChange, setOverlayIdToChange] = useState<string | null>(
    null
  );

  // Loading state for image uploads (Pexels download → resize → Firebase upload)
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Brand kits for the image library
  const [userBrandKits, setUserBrandKits] = useState<BrandKit[]>([]);
  const [selectedBrandKitId, setSelectedBrandKitId] = useState<string | null>(
    activeBrandKitId ?? null
  );
  const hasUserSelectedBrandKitRef = useRef(false);
  const previousDraftIdForBrandKitRef = useRef(draftId);
  const activeBrandKit = useMemo(() => {
    return (
      userBrandKits.find((kit) => kit.id === selectedBrandKitId) ??
      userBrandKits[0]
    );
  }, [selectedBrandKitId, userBrandKits]);
  const activeBrandColors = useMemo(() => {
    return getBrandColorShortcuts(activeBrandKit?.colors);
  }, [activeBrandKit]);

  const bgFileInputRef = useRef<HTMLInputElement | null>(null);
  const overlayImageInputRef = useRef<HTMLInputElement | null>(null);
  const changeOverlayImageInputRef = useRef<HTMLInputElement | null>(null);
  const overlaySvgInputRef = useRef<HTMLInputElement | null>(null);
  const changeSvgInputRef = useRef<HTMLInputElement | null>(null);
  const [svgIdToChange, setSvgIdToChange] = useState<string | null>(null);
  const previousSelectedItemIndexForOverlayRef = useRef(selectedItemIndex);

  useEffect(() => {
    if (previousSelectedItemIndexForOverlayRef.current !== selectedItemIndex) {
      setSelectedOverlayId(null);
      setIsBackgroundSelected(false);
      setOverlayIdToChange(null);
      setSvgIdToChange(null);
      setLibraryMode(null);
      setShapeLibraryMode(false);
    }
    previousSelectedItemIndexForOverlayRef.current = selectedItemIndex;
  }, [selectedItemIndex]);

  useEffect(() => {
    if (selectedOverlayId) setIsBackgroundSelected(false);
  }, [selectedOverlayId]);

  const mediaItem = mediaItems[selectedItemIndex];
  const isPinterestFirstImageFallback =
    inCarouselRenditionPreview &&
    renditionPreview?.platform === "pinterest" &&
    mediaItems.length > 5;
  const renditionSelectedIndex = isPinterestFirstImageFallback
    ? 0
    : selectedItemIndex;
  const renditionPageCount = isPinterestFirstImageFallback
    ? 1
    : mediaItems.length;
  const renditionMediaItem =
    mediaItems[renditionSelectedIndex] ?? mediaItem ?? mediaItems[0];
  const renditionImageDimensions =
    renditionMediaItem?.metadata?.dimensions ?? naturalImageDimensions;
  // The slide's 9:16 story frame, once its media URLs have resolved
  const storyFrameSlide =
    showStoryFrames && renditionMediaItem && needsStoryFrame(renditionMediaItem)
      ? resolvedVideoSlides[renditionSelectedIndex]
      : undefined;

  useEffect(() => {
    let isMounted = true;
    if (mediaItem?.path) {
      setIsLoadingBackgroundPreview(true);
      getSignedUrl(mediaItem.path)
        .then((url) => {
          if (isMounted) {
            setBackgroundPreviewUrl(url);
            const img = new Image();
            img.onload = () => {
              if (isMounted) {
                setNaturalImageDimensions({
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                });
                setIsLoadingBackgroundPreview(false);
              }
            };
            img.src = url;
          }
        })
        .catch((err) => {
          console.error("Error fetching background image preview URL:", err);
          if (isMounted) {
            setNaturalImageDimensions(null);
            setBackgroundPreviewUrl(null);
            setIsLoadingBackgroundPreview(false);
          }
        });
    } else {
      setNaturalImageDimensions(null);
      setBackgroundPreviewUrl(null);
      setIsLoadingBackgroundPreview(false);
    }
    return () => {
      isMounted = false;
    };
  }, [mediaItem?.path, storage]);

  // Load user's brand kits
  useEffect(() => {
    if (!userId) return;
    let isMounted = true;
    getBrandKitsForUser(userId).then((kits) => {
      if (isMounted) setUserBrandKits(kits);
    });
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (previousDraftIdForBrandKitRef.current !== draftId) {
      hasUserSelectedBrandKitRef.current = false;
      setSelectedBrandKitId(activeBrandKitId ?? null);
      previousDraftIdForBrandKitRef.current = draftId;
    }
  }, [activeBrandKitId, draftId]);

  useEffect(() => {
    if (userBrandKits.length === 0) {
      setSelectedBrandKitId(null);
      return;
    }

    const validBrandKitIds = new Set(userBrandKits.map((kit) => kit.id));
    const rememberedBrandKitId = getStoredActiveBrandKitId(userId);

    setSelectedBrandKitId((currentBrandKitId) => {
      if (
        currentBrandKitId &&
        validBrandKitIds.has(currentBrandKitId) &&
        hasUserSelectedBrandKitRef.current
      ) {
        return currentBrandKitId;
      }

      if (activeBrandKitId && validBrandKitIds.has(activeBrandKitId)) {
        return activeBrandKitId;
      }

      if (currentBrandKitId && validBrandKitIds.has(currentBrandKitId)) {
        return currentBrandKitId;
      }

      if (
        rememberedBrandKitId &&
        validBrandKitIds.has(rememberedBrandKitId)
      ) {
        return rememberedBrandKitId;
      }

      return userBrandKits[0].id;
    });
  }, [activeBrandKitId, userBrandKits, userId]);

  const handleActiveBrandKitChange = useCallback(
    (brandKitId: string) => {
      hasUserSelectedBrandKitRef.current = true;
      setSelectedBrandKitId(brandKitId);
      storeActiveBrandKitId(userId, brandKitId);
    },
    [userId]
  );

  const brandKitColorPickerContext = useMemo<
    BrandKitColorPickerContext | undefined
  >(() => {
    if (userBrandKits.length === 0) return undefined;

    return {
      brandKits: userBrandKits,
      selectedBrandKitId: activeBrandKit?.id ?? selectedBrandKitId,
      onSelectBrandKit: handleActiveBrandKitChange,
    };
  }, [
    activeBrandKit?.id,
    handleActiveBrandKitChange,
    selectedBrandKitId,
    userBrandKits,
  ]);

  useEffect(() => {
    console.log("[ImageCompositeEditor] selectedOverlayId state", {
      selectedOverlayId,
    });
  }, [selectedOverlayId]);

  useEffect(() => {
    if (!selectedOverlayId) {
      return;
    }

    const hasSelectedOverlay = initialOverlays.some(
      (o) => o.id === selectedOverlayId
    );

    if (!hasSelectedOverlay) {
      console.warn(
        "[ImageCompositeEditor] Clearing selected overlay because it no longer exists in overlays",
        {
          selectedOverlayId,
          availableOverlayIds: initialOverlays.map((o) => o.id),
        }
      );
      setSelectedOverlayId(null);
      console.log("[ImageCompositeEditor] setSelectedOverlayId invoked", {
        source: "missing-overlay-guard",
        value: null,
      });
    }
  }, [initialOverlays, selectedOverlayId]);

  useEffect(() => {
    console.log("[ImageCompositeEditor] overlays prop", {
      overlayIds: initialOverlays.map((overlay) => overlay.id),
    });
  }, [initialOverlays]);

  const handleFillColorChange = useCallback(
    async (color: string) => {
      if (!onBackgroundColorChange || !userId || !mediaItem) return;
      const dims = mediaItem.metadata?.dimensions;
      if (!dims || dims.width <= 0 || dims.height <= 0) return;

      setIsUploadingBackground(true);
      try {
        // Generate solid-color image on canvas
        const canvas = document.createElement("canvas");
        canvas.width = dims.width;
        canvas.height = dims.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, dims.width, dims.height);

        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, "image/png")
        );
        if (!blob) return;

        const storagePath = `posts/${userId}/${draftId}/backgrounds/${Date.now()}-fill.png`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, blob);

        onBackgroundColorChange(color, storagePath, {
          dimensions: dims,
          size: blob.size,
        });
      } catch (error) {
        console.error("Error generating fill color background:", error);
        toast({
          title: "Error",
          description: "Failed to update fill color",
          variant: "destructive",
        });
      } finally {
        setIsUploadingBackground(false);
      }
    },
    [onBackgroundColorChange, userId, draftId, mediaItem, storage, toast]
  );

  const handleBackgroundFilterChange = useCallback(
    (filter: import("../models/Post").ImageFilter | undefined) => {
      saveSnapshot?.();
      onBackgroundFilterChange?.(filter);
    },
    [onBackgroundFilterChange, saveSnapshot]
  );

  const handleBackgroundImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.[0] || !userId) return;

    // Ensure draftId exists before proceeding with the upload.
    if (!draftId) {
      toast({
        title: "Cannot upload without a draft",
        description: "Please save your post as a draft before adding images.",
        variant: "destructive",
      });
      return;
    }

    const file = event.target.files[0];
    setIsUploadingBackground(true);
    try {
      const storagePath = `posts/${userId}/${draftId}/backgrounds/${Date.now()}-${
        naturalImageDimensions ? file.name.replace(/\.[^.]+$/, "") + ".png" : file.name
      }`;
      const storageRef = ref(storage, storagePath);

      // Load the uploaded image to get its dimensions
      const imageUrl = URL.createObjectURL(file);
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });
      URL.revokeObjectURL(imageUrl);

      let finalBlob: Blob;
      let finalWidth: number;
      let finalHeight: number;

      // Check if we have existing natural dimensions
      if (naturalImageDimensions) {
        // Calculate dimensions to match existing background's aspect ratio
        const targetWidth = naturalImageDimensions.width;
        const targetHeight = naturalImageDimensions.height;

        // Create canvas to resize the image
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Failed to get canvas context");

        // Scale image to fill canvas (cover behavior)
        const imgAspect = img.naturalWidth / img.naturalHeight;
        const canvasAspect = targetWidth / targetHeight;

        let drawWidth, drawHeight, offsetX, offsetY;
        if (imgAspect > canvasAspect) {
          drawHeight = targetHeight;
          drawWidth = drawHeight * imgAspect;
          offsetX = (targetWidth - drawWidth) / 2;
          offsetY = 0;
        } else {
          drawWidth = targetWidth;
          drawHeight = drawWidth / imgAspect;
          offsetX = 0;
          offsetY = (targetHeight - drawHeight) / 2;
        }

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

        // Convert canvas to blob
        finalBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Failed to convert canvas to blob"));
            },
            "image/png"
          );
        });

        finalWidth = targetWidth;
        finalHeight = targetHeight;
      } else {
        // No existing dimensions - use original image
        finalBlob = file;
        finalWidth = img.naturalWidth;
        finalHeight = img.naturalHeight;
      }

      await uploadBytes(storageRef, finalBlob);
      onBackgroundChange(storagePath, {
        dimensions: {
          width: finalWidth,
          height: finalHeight,
        },
        size: finalBlob.size,
      });
    } catch (error) {
      console.error("Error uploading background image:", error);
      toast({ title: "Upload Failed", variant: "destructive" });
    } finally {
      setIsUploadingBackground(false);
    }
  };

  const handleImageOverlayUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.[0] || !userId) return;

    if (!draftId) {
      toast({
        title: "Cannot upload without a draft",
        description: "Please save your post as a draft before adding images.",
        variant: "destructive",
      });
      return;
    }

    const file = event.target.files[0];
    const isSvgFile = file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");

    setIsUploadingOverlay(true);
    try {
      saveSnapshot?.();
      if (isSvgFile) {
        // Handle SVG file - read content and create SvgOverlay
        const svgText = await file.text();
        
        // Parse SVG to extract viewBox and inner content
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
        const svgElement = svgDoc.querySelector("svg");
        
        if (!svgElement) {
          throw new Error("Invalid SVG file");
        }
        
        // Extract viewBox or create default
        let viewBox = svgElement.getAttribute("viewBox") || "0 0 100 100";
        
        // Extract inner content (everything inside the <svg> tag)
        const svgContent = svgElement.innerHTML;
        
        const newOverlay: SvgOverlay = {
          id: generateUniqueId(),
          type: "svg",
          svgContent,
          viewBox,
          position: { x: 10, y: 10 },
          width: 20,
          opacity: 100,
          zIndex: getNextZIndex(initialOverlays),
        };
        handleSetOverlays((prev) => [...prev, newOverlay]);
        setSelectedOverlayId(newOverlay.id);
      } else {
        // Handle regular image file
        const filePath = `posts/${userId}/${draftId}/overlays/${Date.now()}-${
          file.name
        }`;
        const storageRef = ref(storage, filePath);
        await uploadBytes(storageRef, file);

        // Get image dimensions to calculate proper aspect ratio
        const img = new Image();
        const imageUrl = URL.createObjectURL(file);
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = imageUrl;
        });
        URL.revokeObjectURL(imageUrl);

        const { width: overlayWidth, height: overlayHeight } =
          computeOverlaySizeForImage(
            img.naturalWidth,
            img.naturalHeight,
            naturalImageDimensions?.width,
            naturalImageDimensions?.height,
            30,
          );

        const newOverlay: ImageOverlay = {
          id: generateUniqueId(),
          type: "image",
          filePath,
          position: { x: 10, y: 10 },
          width: overlayWidth,
          height: overlayHeight,
          opacity: 100,
          objectFit: "contain",
          zIndex: getNextZIndex(initialOverlays),
        };
        handleSetOverlays((prev) => [...prev, newOverlay]);
        setSelectedOverlayId(newOverlay.id);
      }
    } catch (error) {
      console.error("Error uploading overlay:", error);
      toast({ title: "Upload Failed", variant: "destructive" });
    } finally {
      setIsUploadingOverlay(false);
    }
  };

  const handleAddTextOverlay = () => {
    saveSnapshot?.();
    const newOverlay = createDefaultTextOverlay(initialOverlays);
    handleSetOverlays((prev) => [...prev, newOverlay]);
    setSelectedOverlayId(newOverlay.id);
    console.log("[ImageCompositeEditor] setSelectedOverlayId invoked", {
      source: "handleAddTextOverlay",
      value: newOverlay.id,
    });
  };

  const handleRemoveOverlay = (overlayId: string) => {
    saveSnapshot?.();
    handleSetOverlays((prev) => prev.filter((o) => o.id !== overlayId));
  };

  // Add a new overlay (e.g. paste from clipboard)
  const handleAddOverlay = useCallback(
    (overlay: AnyOverlay) => {
      saveSnapshot?.();
      handleSetOverlays((prev) => [...prev, overlay]);
      setSelectedOverlayId(overlay.id);
    },
    [handleSetOverlays, saveSnapshot]
  );

  // Canvas-driven overlay updates (drag/resize/rotate) — no snapshot (captured at interaction start)
  const handleCanvasOverlayUpdate = useCallback(
    (updatedOverlay: AnyOverlay) => {
      handleSetOverlays((prev) =>
        prev.map((o) => (o.id === updatedOverlay.id ? updatedOverlay : o))
      );
    },
    [handleSetOverlays]
  );

  // Format control changes (sidebar sliders, color pickers, text input)
  // Coalesce rapid changes (e.g. slider drags) into a single undo entry
  const lastFormatSnapshotRef = useRef(0);
  const handleFormatChange = useCallback(
    (updatedOverlay: AnyOverlay) => {
      const now = Date.now();
      if (now - lastFormatSnapshotRef.current > 300) {
        saveSnapshot?.();
      }
      lastFormatSnapshotRef.current = now;
      handleSetOverlays((prev) =>
        prev.map((o) => (o.id === updatedOverlay.id ? updatedOverlay : o))
      );
    },
    [handleSetOverlays, saveSnapshot]
  );

  // onInteractionStart callback for canvas — snapshots once per drag/resize/rotate
  const handleInteractionStart = useCallback(() => {
    saveSnapshot?.();
  }, [saveSnapshot]);

  const handleCopyOverlay = (overlayId: string) => {
    const original = initialOverlays.find((o) => o.id === overlayId);
    if (!original) return;
    saveSnapshot?.();
    const newId = generateUniqueId();
    const copiedOverlay = {
      ...JSON.parse(JSON.stringify(original)),
      id: newId,
      position: {
        x: (original.position.x + 5) % 90,
        y: (original.position.y + 5) % 90,
      },
      zIndex: getNextZIndex(initialOverlays),
    };
    handleSetOverlays((prev) => [...prev, copiedOverlay]);
    setSelectedOverlayId(newId);
    console.log("[ImageCompositeEditor] setSelectedOverlayId invoked", {
      source: "handleCopyOverlay",
      value: newId,
    });
  };

  const handleChangeOverlayImageUpload = (overlayId: string) => {
    setOverlayIdToChange(overlayId);

    if (changeOverlayImageInputRef.current) {
      try {
        changeOverlayImageInputRef.current.click();
      } catch (error) {
        console.error("[ImageCompositeEditor] Error clicking input:", error);
      }
    } else {
      console.error("[ImageCompositeEditor] File input ref is null");
    }
  };

  const handleChangeOverlayBrowseLibrary = (overlayId: string) => {
    setOverlayIdToChange(overlayId);
    setLibraryMode("overlay-change");
  };

  const handleOverlayFillColorChange = useCallback(
    async (overlayId: string, color: string) => {
      if (!userId) return;
      const overlay = initialOverlays.find(
        (o) => o.id === overlayId && o.type === "image"
      ) as ImageOverlay | undefined;
      if (!overlay) return;

      // Calculate pixel dimensions from percentage-based overlay dimensions
      const dims = mediaItem?.metadata?.dimensions;
      if (!dims || dims.width <= 0 || dims.height <= 0) return;
      const pxWidth = Math.round((overlay.width * dims.width) / 100);
      const pxHeight = Math.round(
        ((overlay.height || overlay.width) * dims.height) / 100
      );

      try {
        const canvas = document.createElement("canvas");
        canvas.width = pxWidth;
        canvas.height = pxHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, pxWidth, pxHeight);

        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, "image/png")
        );
        if (!blob) return;

        const storagePath = `posts/${userId}/${draftId}/overlays/${Date.now()}-fill-${overlayId}.png`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, blob);

        saveSnapshot?.();
        handleSetOverlays(
          initialOverlays.map((o) =>
            o.id === overlayId
              ? { ...o, filePath: storagePath, sourceColor: color }
              : o
          )
        );
      } catch (error) {
        console.error("Error generating fill color for overlay:", error);
        toast({
          title: "Error",
          description: "Failed to update overlay fill color",
          variant: "destructive",
        });
      }
    },
    [userId, draftId, initialOverlays, mediaItem, storage, toast, saveSnapshot, handleSetOverlays]
  );

  const handleChangeOverlayImageUploadFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.[0] || !userId || !overlayIdToChange) return;

    if (!draftId) {
      toast({
        title: "Cannot upload without a draft",
        description: "Please save your post as a draft before changing images.",
        variant: "destructive",
      });
      return;
    }

    const file = event.target.files[0];
    const overlayToChange = initialOverlays.find(
      (o) => o.id === overlayIdToChange
    );
    if (!overlayToChange || !isImageOverlay(overlayToChange)) return;

    setIsUploadingOverlay(true);
    try {
      saveSnapshot?.();

      const filePath = `posts/${userId}/${draftId}/overlays/${Date.now()}-${
        file.name
      }`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);

      const updatedOverlay = applyOverlayImageReplacement(overlayToChange, {
        filePath,
      });
      handleSetOverlays((prev) =>
        prev.map((o) => (o.id === overlayIdToChange ? updatedOverlay : o))
      );

      toast({
        title: "Success",
        description: "Image overlay updated",
      });
    } catch (error) {
      console.error("Error uploading replacement image:", error);
      toast({ title: "Upload Failed", variant: "destructive" });
    } finally {
      setIsUploadingOverlay(false);
      setOverlayIdToChange(null);
      if (changeOverlayImageInputRef.current) {
        changeOverlayImageInputRef.current.value = "";
      }
    }
  };

  // SVG overlay upload handler
  const handleSvgOverlayUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    if (!file) return;

    // Validate SVG file
    if (!file.name.toLowerCase().endsWith(".svg") && file.type !== "image/svg+xml") {
      toast({
        title: "Invalid File",
        description: "Please upload an SVG file.",
        variant: "destructive",
      });
      return;
    }

    if (!userId || !draftId) {
      toast({
        title: "Cannot upload",
        description: "Please save your post as a draft first.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingOverlay(true);
    try {
      saveSnapshot?.();

      // Read the SVG file content
      const svgText = await file.text();

      // Extract viewBox from SVG content
      const viewBoxMatch = svgText.match(/viewBox=["']([^"']+)["']/);
      const viewBox = viewBoxMatch ? viewBoxMatch[1] : "0 0 100 100";

      // Extract inner content (everything inside the <svg> tag)
      const contentMatch = svgText.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
      const svgContent = contentMatch ? contentMatch[1].trim() : svgText;

      const newOverlay: SvgOverlay = {
        id: generateUniqueId(),
        type: "svg",
        svgContent,
        viewBox,
        position: { x: 10, y: 10 },
        width: 30,
        opacity: 100,
        fillColor: "#000000", // Default fill color
        zIndex: getNextZIndex(initialOverlays),
      };

      handleSetOverlays((prev) => [...prev, newOverlay]);
      setSelectedOverlayId(newOverlay.id);
    } catch (error) {
      console.error("Error uploading SVG overlay:", error);
      toast({ title: "Upload Failed", variant: "destructive" });
    } finally {
      setIsUploadingOverlay(false);
      if (overlaySvgInputRef.current) {
        overlaySvgInputRef.current.value = "";
      }
    }
  };

  const handleChangeSvgUpload = (overlayId: string) => {
    setSvgIdToChange(overlayId);
    changeSvgInputRef.current?.click();
  };

  const handleChangeSvgUploadFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.[0] || !userId || !svgIdToChange) return;

    if (!draftId) {
      toast({
        title: "Cannot upload",
        description: "Please save your post as a draft first.",
        variant: "destructive",
      });
      return;
    }

    const file = event.target.files[0];
    const overlayToChange = initialOverlays.find((o) => o.id === svgIdToChange);
    if (!overlayToChange || !isSvgOverlay(overlayToChange)) return;

    // Validate SVG file
    if (!file.name.toLowerCase().endsWith(".svg") && file.type !== "image/svg+xml") {
      toast({
        title: "Invalid File",
        description: "Please upload an SVG file.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingOverlay(true);
    try {
      saveSnapshot?.();

      // Read the SVG file content
      const svgText = await file.text();

      // Extract viewBox from SVG content
      const viewBoxMatch = svgText.match(/viewBox=["']([^"']+)["']/);
      const viewBox = viewBoxMatch ? viewBoxMatch[1] : "0 0 100 100";

      // Extract inner content (everything inside the <svg> tag)
      const contentMatch = svgText.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
      const svgContent = contentMatch ? contentMatch[1].trim() : svgText;

      const updatedOverlay: SvgOverlay = {
        ...overlayToChange,
        svgContent,
        viewBox,
        imageStoragePath: undefined, // Clear pre-rendered image since SVG changed
      };
      handleSetOverlays((prev) =>
        prev.map((o) => (o.id === svgIdToChange ? updatedOverlay : o))
      );

      toast({
        title: "Success",
        description: "SVG overlay updated",
      });
    } catch (error) {
      console.error("Error uploading replacement SVG:", error);
      toast({ title: "Upload Failed", variant: "destructive" });
    } finally {
      setIsUploadingOverlay(false);
      setSvgIdToChange(null);
      if (changeSvgInputRef.current) {
        changeSvgInputRef.current.value = "";
      }
    }
  };

  // Handle shape selection from the shape library
  const handleShapeSelect = (shape: ShapeDefinition) => {
    saveSnapshot?.();
    const newOverlay: SvgOverlay = {
      id: generateUniqueId(),
      type: "svg",
      svgContent: shape.content, // Store raw SVG content
      viewBox: shape.viewBox, // Store viewBox for proper scaling
      position: { x: 10, y: 10 },
      width: 20,
      opacity: 100,
      fillColor: "#000000", // Default fill color for predefined shapes
      zIndex: getNextZIndex(initialOverlays),
    };

    handleSetOverlays((prev) => [...prev, newOverlay]);
    setSelectedOverlayId(newOverlay.id);
    setShapeLibraryMode(false);
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reversed overlays for display (top of list = top layer)
  const displayOverlays = useMemo(() => {
    return [...initialOverlays].reverse();
  }, [initialOverlays]);

  // Handle reordering overlays via drag and drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    saveSnapshot?.();

    // Find indices in the display array (reversed)
    const oldIndex = displayOverlays.findIndex((o) => o.id === active.id);
    const newIndex = displayOverlays.findIndex((o) => o.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      // Reorder the display array
      const reorderedDisplay = arrayMove(displayOverlays, oldIndex, newIndex);

      // Reverse back to get the actual storage order and sync zIndex
      const finalOrder = [...reorderedDisplay].reverse();

      handleSetOverlays(syncZIndexToArrayOrder(finalOrder));
    }
  };

  const handlePexelsImageSelect = async (image: PexelsImage) => {
    if (!userId || !draftId || !currentUser) {
      toast({
        title: "Cannot upload",
        description: "Please save your post as a draft first.",
        variant: "destructive",
      });
      return;
    }

    const isBackground = libraryMode === "background";
    const isOverlayChange = libraryMode === "overlay-change";

    // Close library immediately so the user sees the control panel with loading state
    setLibraryMode(null);
    setIsUploadingImage(true);
    let processingStage:
      | "download"
      | "decode"
      | "canvas"
      | "upload"
      | "apply" = "download";

    try {
      saveSnapshot?.();

      // Download through the authenticated Wavegen API so browser-specific
      // CORS, extensions, and network policies cannot block Pexels directly.
      const idToken = await currentUser.getIdToken();
      const imageBlob = await PexelsService.downloadImage(
        image.src.large,
        idToken,
        image.id
      );

      // Load the downloaded image
      processingStage = "decode";
      const imageUrl = URL.createObjectURL(imageBlob);
      const img = new Image();
      try {
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () =>
            reject(new Error("Browser could not decode the stock image"));
          img.src = imageUrl;
        });
      } finally {
        URL.revokeObjectURL(imageUrl);
      }

      let finalBlob: Blob;
      let finalWidth: number;
      let finalHeight: number;

      if (isBackground && naturalImageDimensions) {
        processingStage = "canvas";
        // Resize to match original background dimensions
        const targetWidth = naturalImageDimensions.width;
        const targetHeight = naturalImageDimensions.height;

        // Create canvas and resize image to match original dimensions
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Failed to get canvas context");
        }

        // Scale image to fill canvas (cover behavior)
        const imgAspect = img.naturalWidth / img.naturalHeight;
        const canvasAspect = targetWidth / targetHeight;

        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgAspect > canvasAspect) {
          // Image is wider - scale to height
          drawHeight = targetHeight;
          drawWidth = drawHeight * imgAspect;
          offsetX = (targetWidth - drawWidth) / 2;
          offsetY = 0;
        } else {
          // Image is taller - scale to width
          drawWidth = targetWidth;
          drawHeight = drawWidth / imgAspect;
          offsetX = 0;
          offsetY = (targetHeight - drawHeight) / 2;
        }

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

        // Convert canvas to blob
        finalBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Failed to convert canvas to blob"));
            },
            "image/png"
          );
        });

        finalWidth = targetWidth;
        finalHeight = targetHeight;
      } else {
        // Use original image as-is
        finalBlob = imageBlob;
        finalWidth = img.naturalWidth;
        finalHeight = img.naturalHeight;
      }

      // Upload to Firebase Storage
      const fileName = `pexels-${image.id}.${finalBlob.type === "image/png" ? "png" : finalBlob.type === "image/webp" ? "webp" : "jpg"}`;
      const storagePath = isBackground
        ? `posts/${userId}/${draftId}/backgrounds/${Date.now()}-${fileName}`
        : `posts/${userId}/${draftId}/overlays/${Date.now()}-${fileName}`;

      const storageRef = ref(storage, storagePath);
      processingStage = "upload";
      await uploadBytes(storageRef, finalBlob);

      processingStage = "apply";
      if (isBackground) {
        // Update background with final dimensions
        onBackgroundChange(storagePath, {
          dimensions: {
            width: finalWidth,
            height: finalHeight,
          },
          size: finalBlob.size,
        });
      } else if (isOverlayChange && overlayIdToChange) {
        // Change existing overlay image
        const overlayToChange = initialOverlays.find(
          (o) => o.id === overlayIdToChange
        );
        if (overlayToChange && isImageOverlay(overlayToChange)) {
          const updatedOverlay = applyOverlayImageReplacement(overlayToChange, {
            filePath: storagePath,
            objectFit: "cover",
          });
          handleSetOverlays((prev) =>
            prev.map((o) => (o.id === overlayIdToChange ? updatedOverlay : o))
          );
        }
      } else {
        // Add as overlay
        const { width: overlayWidth, height: overlayHeight } =
          computeOverlaySizeForImage(
            img.naturalWidth,
            img.naturalHeight,
            naturalImageDimensions?.width,
            naturalImageDimensions?.height,
            30,
          );
        const newOverlay: ImageOverlay = {
          id: generateUniqueId(),
          type: "image",
          filePath: storagePath,
          position: { x: 10, y: 10 },
          width: overlayWidth,
          height: overlayHeight,
          opacity: 100,
          objectFit: "contain",
          zIndex: getNextZIndex(initialOverlays),
        };
        handleSetOverlays((prev) => [...prev, newOverlay]);
        setSelectedOverlayId(newOverlay.id);
        console.log("[ImageCompositeEditor] setSelectedOverlayId invoked", {
          source: "handlePexelsImageSelect-new",
          value: newOverlay.id,
        });
      }

      setOverlayIdToChange(null);
    } catch (error) {
      const errorName =
        error instanceof Error ? error.name : "UnknownPexelsImageError";
      const errorMessage =
        error instanceof Error ? error.message : "Unknown stock image error";
      const proxyError =
        error instanceof PexelsImageDownloadError ? error : undefined;

      console.error("Error processing Pexels image:", {
        stage: processingStage,
        imageId: image.id,
        error,
      });
      posthog.capture("pexels_image_process_failed", {
        image_id: image.id,
        image_mode: isBackground
          ? "background"
          : isOverlayChange
            ? "overlay_change"
            : "overlay_add",
        processing_stage: processingStage,
        error_name: errorName,
        error_message: errorMessage,
        error_code: proxyError?.code,
        http_status: proxyError?.status,
      });

      const failureDescription =
        processingStage === "download"
          ? "Could not download this stock image. Please try again."
          : processingStage === "decode"
            ? "Your browser could not read this stock image. Please try another."
            : processingStage === "canvas"
              ? "Could not resize this stock image. Please try another."
              : processingStage === "upload"
                ? "The image downloaded but could not be saved. Please retry."
                : "The image was saved but could not be applied. Please retry.";
      toast({
        title: "Stock image failed",
        description: failureDescription,
        variant: "destructive",
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleLibraryClose = () => {
    setLibraryMode(null);
    setOverlayIdToChange(null);
  };

  const handleArticleImageSelect = async (image: ExtractedArticleImage) => {
    const isBackground = libraryMode === "background";
    const isOverlayChange = libraryMode === "overlay-change";

    try {
      saveSnapshot?.();
      if (isBackground) {
        // Use article image as background
        // If we have existing background dimensions, resize the article image to match (cover behavior)
        if (naturalImageDimensions && userId && draftId) {
          const targetWidth = naturalImageDimensions.width;
          const targetHeight = naturalImageDimensions.height;

          // Load the article image
          const imageUrl = await getSignedUrl(image.storagePath);
          const img = new window.Image();
          img.crossOrigin = "anonymous";
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = reject;
            img.src = imageUrl;
          });

          // Create canvas to resize the image with cover behavior
          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Failed to get canvas context");

          // Scale image to fill canvas (cover behavior)
          const imgAspect = img.naturalWidth / img.naturalHeight;
          const canvasAspect = targetWidth / targetHeight;

          let drawWidth, drawHeight, offsetX, offsetY;
          if (imgAspect > canvasAspect) {
            drawHeight = targetHeight;
            drawWidth = drawHeight * imgAspect;
            offsetX = (targetWidth - drawWidth) / 2;
            offsetY = 0;
          } else {
            drawWidth = targetWidth;
            drawHeight = drawWidth / imgAspect;
            offsetX = 0;
            offsetY = (targetHeight - drawHeight) / 2;
          }

          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

          // Convert canvas to blob and upload
          const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (b) => (b ? resolve(b) : reject(new Error("Failed to create blob"))),
              "image/png"
            );
          });

          const storagePath = `posts/${userId}/${draftId}/backgrounds/${Date.now()}-article-bg.png`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, blob);

          onBackgroundChange(storagePath, {
            dimensions: { width: targetWidth, height: targetHeight },
            size: blob.size,
          });
        } else {
          // No existing dimensions - use article image as-is
          onBackgroundChange(image.storagePath, {
            dimensions: image.dimensions,
            size: image.dimensions.width * image.dimensions.height,
          });
        }
      } else if (isOverlayChange && overlayIdToChange) {
        // Change existing overlay image
        const overlayToChange = initialOverlays.find(
          (o) => o.id === overlayIdToChange
        );
        if (overlayToChange && isImageOverlay(overlayToChange)) {
          const updatedOverlay = applyOverlayImageReplacement(overlayToChange, {
            filePath: image.storagePath,
            objectFit: "contain", // Article images should use contain to show full image
          });
          handleSetOverlays((prev) =>
            prev.map((o) => (o.id === overlayIdToChange ? updatedOverlay : o))
          );
        }
      } else {
        // Add as new overlay
        const { width: overlayWidth, height: overlayHeight } =
          computeOverlaySizeForImage(
            image.dimensions?.width,
            image.dimensions?.height,
            naturalImageDimensions?.width,
            naturalImageDimensions?.height,
            40,
          );
        const newOverlay: ImageOverlay = {
          id: generateUniqueId(),
          type: "image",
          filePath: image.storagePath,
          position: { x: 10, y: 10 },
          width: overlayWidth,
          height: overlayHeight,
          opacity: 100,
          objectFit: "contain", // Article images should use contain
          zIndex: getNextZIndex(initialOverlays),
        };
        handleSetOverlays((prev) => [...prev, newOverlay]);
        setSelectedOverlayId(newOverlay.id);
      }

      // Clear library mode
      setLibraryMode(null);
      setOverlayIdToChange(null);
    } catch (error) {
      console.error("Error using article image:", error);
      toast({
        title: "Failed",
        description: "Could not use the selected image",
        variant: "destructive",
      });
    }
  };

  const handleBrandKitImageSelect = async (asset: BrandAsset) => {
    const isBackground = libraryMode === "background";
    const isOverlayChange = libraryMode === "overlay-change";

    try {
      saveSnapshot?.();
      if (isBackground) {
        // Use brand kit image as background — same resize logic as article images
        if (naturalImageDimensions && userId && draftId) {
          const targetWidth = naturalImageDimensions.width;
          const targetHeight = naturalImageDimensions.height;

          const imageUrl = await getSignedUrl(asset.storagePath);
          const img = new window.Image();
          img.crossOrigin = "anonymous";
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = reject;
            img.src = imageUrl;
          });

          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Failed to get canvas context");

          const imgAspect = img.naturalWidth / img.naturalHeight;
          const canvasAspect = targetWidth / targetHeight;
          let drawWidth, drawHeight, offsetX, offsetY;
          if (imgAspect > canvasAspect) {
            drawHeight = targetHeight;
            drawWidth = drawHeight * imgAspect;
            offsetX = (targetWidth - drawWidth) / 2;
            offsetY = 0;
          } else {
            drawWidth = targetWidth;
            drawHeight = drawWidth / imgAspect;
            offsetX = 0;
            offsetY = (targetHeight - drawHeight) / 2;
          }
          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

          const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (b) =>
                b ? resolve(b) : reject(new Error("Failed to create blob")),
              "image/png"
            );
          });

          const storagePath = `posts/${userId}/${draftId}/backgrounds/${Date.now()}-brandkit-bg.png`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, blob);

          onBackgroundChange(storagePath, {
            dimensions: { width: targetWidth, height: targetHeight },
            size: blob.size,
          });
        } else {
          onBackgroundChange(asset.storagePath, {
            dimensions: asset.dimensions,
            size: asset.dimensions.width * asset.dimensions.height,
          });
        }
      } else if (isOverlayChange && overlayIdToChange) {
        const overlayToChange = initialOverlays.find(
          (o) => o.id === overlayIdToChange
        );
        if (overlayToChange && isImageOverlay(overlayToChange)) {
          const updatedOverlay = applyOverlayImageReplacement(overlayToChange, {
            filePath: asset.storagePath,
            objectFit: "contain",
          });
          handleSetOverlays((prev) =>
            prev.map((o) =>
              o.id === overlayIdToChange ? updatedOverlay : o
            )
          );
        }
      } else {
        // Add as new overlay
        const { width: overlayWidth, height: overlayHeight } =
          computeOverlaySizeForImage(
            asset.dimensions?.width,
            asset.dimensions?.height,
            naturalImageDimensions?.width,
            naturalImageDimensions?.height,
            40,
          );
        const newOverlay: ImageOverlay = {
          id: generateUniqueId(),
          type: "image",
          filePath: asset.storagePath,
          position: { x: 10, y: 10 },
          width: overlayWidth,
          height: overlayHeight,
          opacity: 100,
          objectFit: "contain",
          zIndex: getNextZIndex(initialOverlays),
        };
        handleSetOverlays((prev) => [...prev, newOverlay]);
        setSelectedOverlayId(newOverlay.id);
      }

      setLibraryMode(null);
      setOverlayIdToChange(null);
    } catch (error) {
      console.error("Error using brand kit image:", error);
      toast({
        title: "Failed",
        description: "Could not use the selected image",
        variant: "destructive",
      });
    }
  };

  const selectedOverlay = useMemo(
    () => initialOverlays.find((o) => o.id === selectedOverlayId) || null,
    [initialOverlays, selectedOverlayId]
  );

  // Resolve preview URL for the selected image overlay.
  // Only re-fetch when the file path actually changes, not on every overlay property change
  // (e.g. rotation, opacity), to avoid clearing and re-loading the image preview on every slider tick.
  const selectedOverlayFilePath = selectedOverlay && isImageOverlay(selectedOverlay)
    ? selectedOverlay.filePath
    : undefined;
  const [selectedOverlayPreviewUrl, setSelectedOverlayPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    let isMounted = true;
    setSelectedOverlayPreviewUrl(null);
    if (selectedOverlayFilePath) {
      const storageRef = ref(getStorage(), selectedOverlayFilePath);
      getSignedUrl(storageRef.fullPath)
        .then((url) => { if (isMounted) setSelectedOverlayPreviewUrl(url); })
        .catch(() => { if (isMounted) setSelectedOverlayPreviewUrl(null); });
    }
    return () => { isMounted = false; };
  }, [selectedOverlayFilePath]);

  const overlayPreviewUrls = useMemo(() => {
    const basePreviewUrls = selectedOverlayPreviewUrls || {};
    return Object.keys(basePreviewUrls).length > 0 ? basePreviewUrls : undefined;
  }, [selectedOverlayPreviewUrls]);

  const previewStyle = useMemo(() => {
    const aspectRatio = naturalImageDimensions
      ? naturalImageDimensions.width / naturalImageDimensions.height
      : 1080 / 1350; // Default 4:5 aspect ratio

    return {
      height: "60dvh",
      width: `${60 * aspectRatio}dvh`,
      maxWidth: "100%",
      aspectRatio: `${aspectRatio}`,
      position: "relative",
      overflow: "visible",
    } as React.CSSProperties;
  }, [naturalImageDimensions]);

  const videoPreviewStyle = useMemo(() => {
    const aspectRatio = 9 / 16;
    return {
      height: "70dvh",
      width: `${70 * aspectRatio}dvh`,
      maxWidth: "100%",
      aspectRatio: `${aspectRatio}`,
      position: "relative",
      overflow: "hidden",
    } as React.CSSProperties;
  }, []);

  // Rendition-preview media sizing. Card chrome (header, caption, action
  // bar ≈190px) stacks around the media, so its height yields to the chrome
  // only when the viewport is short — otherwise it matches the editor
  // canvas. TikTok's chrome overlays instead, so it stays tall. Width
  // resolves from aspect-ratio (no explicit width, since height may be a
  // calc()).
  const renditionMediaStyle = useMemo(() => {
    const aspectRatio = renditionImageDimensions
      ? renditionImageDimensions.width / renditionImageDimensions.height
      : 1080 / 1350;
    // Width must be explicit: block-level width:auto stretches to the parent
    // rather than resolving from aspect-ratio, mis-scaling the canvas
    const heightExpr = "min(58dvh, 78dvh - 190px)";
    return {
      height: heightExpr,
      width: `calc(${heightExpr} * ${aspectRatio})`,
      maxWidth: "100%",
      aspectRatio: `${aspectRatio}`,
      position: "relative",
      overflow: "hidden",
    } as React.CSSProperties;
  }, [renditionImageDimensions]);

  // TikTok photo posts: the slide is sized to the width of a 9:16 phone
  // viewport (PlatformPostFrame builds the surrounding screen as a flex
  // column: nav / photo / dots / caption)
  const TIKTOK_VIEW_HEIGHT = "72dvh";
  const tiktokSlideStyle = useMemo(() => {
    const aspectRatio = renditionImageDimensions
      ? renditionImageDimensions.width / renditionImageDimensions.height
      : 1080 / 1350;
    // Fit by width; guard against slides taller than 9:16 overflowing
    const fitRatio = Math.max(aspectRatio, 9 / 16);
    const width = `calc(${TIKTOK_VIEW_HEIGHT} * ${9 / 16})`;
    return {
      width,
      height: `calc(${width} / ${fitRatio})`,
      aspectRatio: `${aspectRatio}`,
      position: "relative",
      overflow: "hidden",
    } as React.CSSProperties;
  }, [renditionImageDimensions]);

  // A 9:16 story frame fills the whole phone viewport
  const storyFrameStyle = useMemo(() => {
    const width = `calc(${TIKTOK_VIEW_HEIGHT} * ${9 / 16})`;
    return {
      width,
      height: TIKTOK_VIEW_HEIGHT,
      aspectRatio: `${9 / 16}`,
      position: "relative",
      overflow: "hidden",
    } as React.CSSProperties;
  }, []);

  // Overlay-chrome platforms (TikTok, Instagram Reels) show the vertical
  // video full-bleed, so the player stays tall
  const framedVideoStyle = useMemo(() => {
    const aspectRatio = 9 / 16;
    const heightExpr = "66dvh";
    return {
      height: heightExpr,
      width: `calc(${heightExpr} * ${aspectRatio})`,
      maxWidth: "100%",
      aspectRatio: `${aspectRatio}`,
      position: "relative",
      overflow: "hidden",
    } as React.CSSProperties;
  }, []);

  // LinkedIn/Facebook feeds display vertical video inside a ~4:5 media area
  // with side bars (full 9:16 only appears on click-through). The card's
  // media box is 4:5; the 9:16 player fits by height inside it.
  const CARD_VIDEO_HEIGHT = "min(56dvh, 80dvh - 190px)";
  const feedVideoBoxStyle = useMemo(() => {
    const aspectRatio = 4 / 5;
    return {
      height: CARD_VIDEO_HEIGHT,
      width: `calc(${CARD_VIDEO_HEIGHT} * ${aspectRatio})`,
      maxWidth: "100%",
      aspectRatio: `${aspectRatio}`,
      position: "relative",
      overflow: "hidden",
    } as React.CSSProperties;
  }, [CARD_VIDEO_HEIGHT]);
  const feedVideoInnerStyle = useMemo(() => {
    const aspectRatio = 9 / 16;
    return {
      height: CARD_VIDEO_HEIGHT,
      width: `calc(${CARD_VIDEO_HEIGHT} * ${aspectRatio})`,
      aspectRatio: `${aspectRatio}`,
      position: "relative",
      overflow: "hidden",
    } as React.CSSProperties;
  }, [CARD_VIDEO_HEIGHT]);

  // Non-YouTube video renditions render inside the platform's feed-card /
  // overlay / story chrome; YouTube keeps its in-frame Shorts chrome
  const useVideoPlatformFrame =
    inRenditionPreview && renditionPreview?.platform !== "youtube";
  // Stories are full 9:16 — the feed pillarbox only applies to feed cards
  const useFeedVideoBox =
    useVideoPlatformFrame &&
    renditionPreview?.format !== "story" &&
    (renditionPreview?.platform === "linkedin" ||
      renditionPreview?.platform === "facebook");

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if target is input/textarea/contentEditable
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || e.key.toLowerCase() !== "z") return;

      e.preventDefault();
      if (e.shiftKey) {
        redo?.();
      } else {
        undo?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undo, redo]);

  return (
    <div className="p-4 flex flex-col h-full overflow-auto">
      <div className="flex flex-col h-full w-full gap-6 lg:flex-row">
        <div className="flex flex-col min-w-0 flex-1">
          <div
            className={cn(
              "flex-1 flex flex-col items-center",
              // Drill-down top-anchors the stage so the frame sits at the
              // same position for every platform/format; editing centers
              inRenditionPreview ? "justify-start pt-2" : "justify-center"
            )}
          >
            {/* Rendition preview (publish modal): the modal already names the
                channel, so only the watermark upsell shows here. */}
            {inRenditionPreview && renditionPreview && showWatermarkUpsell && (
              <div className="mb-2 flex justify-center">
                <button
                  type="button"
                  onClick={() => navigateToUpgrade({ reason: "watermark" })}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Remove watermark — Upgrade
                </button>
              </div>
            )}
            {/* Post type toggle */}
            {mediaItems.length >= 1 && draftId && !inRenditionPreview && (
              <div className="mb-4 flex flex-col items-center gap-2">
                <PostFormatToggle
                  label="Default format"
                  mediaCount={mediaItems.length}
                  loading={isLoadingChoreography}
                  video={isVideoPost}
                  onChange={(video) => { if (video !== isVideoPost) void (video ? handleConvertToVideo() : handleRevertToCarousel()); }}
                  disabled={isLoadingChoreography || isRevertingToCarousel || videoSettingsDisabled}
                />
                <p className="text-xs text-muted-foreground">You can choose a different format for each platform.</p>
              </div>
            )}

            {/* Choreography invalidation banner — hidden in drill-down,
                where regeneration would be a post-wide change */}
            {isVideoPost && !inRenditionPreview && showInvalidationBanner && (
              <div className="w-full mb-2 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                <span className="flex-1">Slides have changed. Regenerate video?</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={async () => {
                    setVideoChoreography(null);
                    setVideoMusicPath(undefined);
                    setVideoMusicUrl(undefined);
                    await handleConvertToVideo(true);
                  }}
                  disabled={isLoadingChoreography}
                >
                  {isLoadingChoreography ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Regenerate
                </Button>
              </div>
            )}

            {/* Stage — re-keyed per drilled channel/format so switches get a
                single quiet entrance instead of a hard swap. Constant key in
                normal editing so the canvas never remounts mid-edit. */}
            <div
              key={
                inRenditionPreview && renditionPreview
                  ? `${renditionPreview.platform}:${renditionPreview.format}`
                  : "editor"
              }
              className="w-full flex flex-col items-center animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none"
            >
            {/* Video stage — video posts and per-channel rendition previews */}
            {showVideoStage ? (
              <div className="w-full flex flex-col items-center gap-3">
              <div className="w-full flex items-center justify-center relative">
                {(() => {
                  /* Video player — always rendered, dimmed+paused when
                     editing. Non-YouTube renditions wrap it in the
                     platform's native chrome. */
                  const videoFrame = (
                <div
                  style={
                    useFeedVideoBox
                      ? feedVideoInnerStyle
                      : useVideoPlatformFrame
                        ? framedVideoStyle
                        : videoPreviewStyle
                  }
                  className={`relative overflow-hidden flex items-center justify-center shadow-lg ring-1 ring-black/10 rounded-sm transition-all duration-300 ${
                    videoEditingSlide ? 'opacity-30 scale-[0.97] blur-[2px]' : ''
                  }`}
                >
                  {isLoadingChoreography || isLoadingRendition ? (
                    <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span>
                        {inRenditionPreview
                          ? "Choreographing your slides…"
                          : "Preparing video…"}
                      </span>
                    </div>
                  ) : inRenditionPreview && renditionError ? (
                    <div className="flex flex-col items-center gap-3 px-6 text-center text-sm text-muted-foreground">
                      <span>{renditionError}</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setRenditionRetryToken((t) => t + 1)}
                      >
                        Try again
                      </Button>
                    </div>
                  ) : speedAdjustedChoreography && resolvedVideoSlides.length >= 1 ? (
                    <>
                      <CarouselVideoPreview
                        key={videoEditingSlide ? 'paused' : `playing-${effectiveSpeed}`}
                        slides={resolvedVideoSlides}
                        choreography={speedAdjustedChoreography}
                        containerWidth={resolvedVideoSlides[0]?.dimensions.width ?? 1080}
                        containerHeight={resolvedVideoSlides[0]?.dimensions.height ?? 1350}
                        layoutEngine="remotion"
                        autoPlay={inRenditionPreview}
                        startOnCover={!inRenditionPreview}
                        musicUrl={videoEditingSlide || effectiveAudioMuted ? undefined : videoMusicUrl}
                        style={{ width: '100%', height: '100%' }}
                      />
                      {/* Native platform chrome — visual only, never
                          intercepts the player; bottom padding leaves the
                          scrub bar clear */}
                      {inRenditionPreview && renditionPreview?.platform === "youtube" && (
                        <>
                          <div className="absolute inset-x-0 bottom-0 pointer-events-none">
                            <div className="bg-gradient-to-t from-black/70 to-transparent px-3 pt-10 pb-12 pr-14">
                              {renditionPreview?.channelName && (
                                <div className="text-white text-xs font-medium mb-1">
                                  @{renditionPreview.channelName}
                                </div>
                              )}
                              {renditionPreview?.title && (
                                <div className="text-white/90 text-sm leading-snug line-clamp-2">
                                  {renditionPreview.title}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="absolute right-2 bottom-24 pointer-events-none flex flex-col items-center gap-4 text-white/90">
                            <ThumbsUp className="h-5 w-5 drop-shadow" />
                            <ThumbsDown className="h-5 w-5 drop-shadow" />
                            <MessageCircle className="h-5 w-5 drop-shadow" />
                            <Share2 className="h-5 w-5 drop-shadow" />
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading slides...
                    </div>
                  )}
                </div>
                  );
                  const framed = useVideoPlatformFrame ? (
                    <PlatformPostFrame
                      platform={renditionPreview!.platform}
                      format={renditionPreview!.format}
                      channelName={renditionPreview?.channelName}
                      profileImage={renditionPreview?.profileImage}
                      caption={renditionPreview?.caption}
                    >
                      {useFeedVideoBox ? (
                        /* Feed pillarbox: 4:5 media area, 9:16 video fit by
                           height — matches how LinkedIn/FB feeds display
                           vertical video */
                        <div
                          style={feedVideoBoxStyle}
                          className="bg-black flex items-center justify-center"
                        >
                          {videoFrame}
                        </div>
                      ) : (
                        videoFrame
                      )}
                    </PlatformPostFrame>
                  ) : (
                    videoFrame
                  );
                  return (
                    <div className="relative">
                      {framed}
                      {/* Settings flyout — pinned to the frame's right edge
                          so the preview stays fully visible while adjusting */}
                      {inRenditionPreview && videoSettingsOpen && (
                        <div className="absolute left-full bottom-0 ml-4 w-80 max-h-full overflow-y-auto rounded-lg border bg-popover p-4 shadow-md z-20 animate-in fade-in slide-in-from-left-1 duration-200 motion-reduce:animate-none">
                          <VideoAudioSettings
                            postId={draftId}
                            persist={false}
                            disabled={videoSettingsDisabled}
                            musicPath={effectiveMusicPath}
                            audioMuted={effectiveAudioMuted}
                            speed={effectiveSpeed}
                            onMusicChange={(path, name) =>
                              renditionPreview?.onVideoOverridesChange?.({
                                videoMusicPath: path,
                                videoMusicName: name,
                              })
                            }
                            onMuteChange={(muted) =>
                              renditionPreview?.onVideoOverridesChange?.({
                                videoAudioMuted: muted,
                              })
                            }
                            onSpeedChange={(s) =>
                              renditionPreview?.onVideoOverridesChange?.({
                                videoSpeed: s,
                              })
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Pop-out slide editor — floats over the dimmed video */}
                {videoEditingSlide && (
                  <div
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center"
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        setVideoEditingSlide(false);
                        setSelectedOverlayId(null);
                      }
                    }}
                  >
                    {/* Slide indicator + close */}
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-muted-foreground">
                        Editing slide {selectedItemIndex + 1} of {mediaItems.length}
                      </span>
                      <button
                        onClick={() => setVideoEditingSlide(false)}
                        className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-muted"
                        title="Back to video"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div
                      className="media-content relative rounded-sm"
                      style={{
                        ...previewStyle,
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.1)',
                      }}
                    >
                      <ScenePreviewCanvasWrapper
                        backgroundImagePath={mediaItem?.path}
                        backgroundImageUrl={
                          selectedItemPreviewUrl || backgroundPreviewUrl || undefined
                        }
                        backgroundFilter={mediaItem?.metadata?.backgroundFilter}
                        overlays={initialOverlays}
                        overlayPreviewUrls={overlayPreviewUrls}
                        onOverlayChange={handleCanvasOverlayUpdate}
                        onInteractionStart={handleInteractionStart}
                        onSelectOverlay={(id) => {
                          setSelectedOverlayId(id);
                        }}
                        selectedOverlayId={selectedOverlayId}
                        referenceMediaWidth={naturalImageDimensions?.width}
                        onEditorModeChange={setEditorMode}
                        onBackgroundImageResolved={onBackgroundImageResolved}
                        onDeleteOverlay={handleRemoveOverlay}
                        onDuplicateOverlay={handleCopyOverlay}
                        onAddOverlay={handleAddOverlay}
                      />
                    </div>
                  </div>
                )}
              </div>
              {/* Drill-down video controls: one quiet chip under the player;
                  full settings open in a flyout beside the frame */}
              {inRenditionPreview && (
                <Button
                  variant={videoSettingsOpen ? "secondary" : "outline"}
                  size="sm"
                  aria-expanded={videoSettingsOpen}
                  className="text-muted-foreground font-normal"
                  onClick={() => setVideoSettingsOpen((open) => !open)}
                >
                  <Music className="h-3.5 w-3.5 mr-2" />
                  {effectiveAudioMuted
                    ? "Muted"
                    : effectiveMusicPath
                      ? effectiveMusicPath
                          .split("/")
                          .pop()
                          ?.replace(/[-_]/g, " ")
                          .replace(/\.\w+$/, "")
                      : "No music"}
                  <span className="mx-1.5 opacity-40">·</span>
                  {effectiveSpeed}x
                </Button>
              )}
              </div>
            ) : inCarouselRenditionPreview ? (
              /* Carousel rendition preview — read-only slides in the
                 platform's native chrome; navigation only, no editing
                 surface */
              <div className="w-full flex flex-col items-center gap-2">
                <div className="w-full flex items-center justify-center gap-2">
                  {/* Slots stay reserved (invisible) so the media doesn't
                      shift when a platform has no paging (FB collage) */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "shrink-0 h-8 w-8 rounded-full",
                      (renditionPageCount <= 1 || useFbCollage) && "invisible"
                    )}
                    disabled={selectedItemIndex === 0}
                    onClick={() => onSelectItem(selectedItemIndex - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <PlatformPostFrame
                    platform={renditionPreview!.platform}
                    format={renditionPreview!.format}
                    channelName={renditionPreview?.channelName}
                    profileImage={renditionPreview?.profileImage}
                    caption={renditionPreview?.caption}
                    title={renditionPreview?.title}
                    pageIndex={useFbCollage ? undefined : renditionSelectedIndex}
                    pageCount={useFbCollage ? undefined : renditionPageCount}
                  >
                  {useFbCollage ? (
                    /* Facebook multi-photo collage (portrait layout): large
                       tile left, two stacked right, "+N" over the last
                       visible tile. Side-by-side keeps the card feed-wide. */
                    (() => {
                      const collageAr = naturalImageDimensions
                        ? naturalImageDimensions.width / naturalImageDimensions.height
                        : 1080 / 1350;
                      // Collage height ≈ left tile height = (2W/3)/ar →
                      // W = H * 3ar/2, bounded by the card's height budget
                      const collageWidth = `calc(min(50dvh, 78dvh - 190px) * ${1.5 * collageAr})`;
                      const tile = (
                        i: number,
                        plusN = 0,
                        style?: React.CSSProperties
                      ) => {
                        const item = mediaItems[i];
                        if (!item) return null;
                        // Each tile keeps its own slide's aspect — uploads
                        // can differ per slide
                        const dims = item.metadata?.dimensions;
                        const tileAr =
                          dims && dims.width && dims.height
                            ? dims.width / dims.height
                            : collageAr;
                        return (
                          <div
                            key={i}
                            className="relative overflow-hidden w-full"
                            style={{ aspectRatio: `${tileAr}`, ...style }}
                          >
                            <ScenePreviewCanvasWrapper
                              backgroundImagePath={item.path}
                              backgroundColor={item.metadata?.backgroundColor}
                              backgroundFilter={item.metadata?.backgroundFilter}
                              overlays={withPreviewWatermark(
                                item.metadata?.overlays ?? [],
                                item
                              )}
                              overlayPreviewUrls={{
                                [WATERMARK_STORAGE_PATH]: WATERMARK_PUBLIC_URL,
                              }}
                              referenceMediaWidth={item.metadata?.dimensions?.width}
                            />
                            {plusN > 0 && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-2xl font-semibold">
                                +{plusN}
                              </div>
                            )}
                          </div>
                        );
                      };
                      return mediaItems.length === 2 ? (
                        <div
                          className="flex gap-0.5 pointer-events-none max-w-full"
                          style={{ width: collageWidth }}
                        >
                          {tile(0, 0, { width: "calc(50% - 1px)" })}
                          {tile(1, 0, { width: "calc(50% - 1px)" })}
                        </div>
                      ) : (
                        <div
                          className="flex items-start gap-0.5 pointer-events-none max-w-full"
                          style={{ width: collageWidth }}
                        >
                          <div style={{ width: "calc(66.667% - 1px)" }}>
                            {tile(0)}
                          </div>
                          <div className="flex-1 flex flex-col gap-0.5">
                            {tile(1)}
                            {tile(2, Math.max(mediaItems.length - 3, 0))}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                  <div
                        className="media-content relative pointer-events-none"
                        style={
                          storyFrameSlide
                            ? storyFrameStyle
                            : usePhoneSlide
                              ? tiktokSlideStyle
                              : renditionMediaStyle
                        }
                      >
                        {storyFrameSlide ? (
                          <StoryFramePreview slide={storyFrameSlide} />
                        ) : (
                        <ScenePreviewCanvasWrapper
                          backgroundImagePath={renditionMediaItem?.path}
                          backgroundImageUrl={
                            isPinterestFirstImageFallback
                              ? undefined
                              : selectedItemPreviewUrl ||
                                backgroundPreviewUrl ||
                                undefined
                          }
                          backgroundFilter={
                            renditionMediaItem?.metadata?.backgroundFilter
                          }
                          overlays={withPreviewWatermark(
                            renditionMediaItem?.metadata?.overlays ?? [],
                            renditionMediaItem
                          )}
                          overlayPreviewUrls={{
                            ...overlayPreviewUrls,
                            [WATERMARK_STORAGE_PATH]: WATERMARK_PUBLIC_URL,
                          }}
                          onOverlayChange={handleCanvasOverlayUpdate}
                          onInteractionStart={handleInteractionStart}
                          onSelectOverlay={() => {}}
                          selectedOverlayId={null}
                          referenceMediaWidth={renditionImageDimensions?.width}
                          onEditorModeChange={setEditorMode}
                          onBackgroundImageResolved={
                            isPinterestFirstImageFallback
                              ? undefined
                              : onBackgroundImageResolved
                          }
                          onDeleteOverlay={handleRemoveOverlay}
                          onDuplicateOverlay={handleCopyOverlay}
                          onAddOverlay={handleAddOverlay}
                        />
                        )}
                      </div>
                  )}
                  </PlatformPostFrame>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "shrink-0 h-8 w-8 rounded-full",
                      (renditionPageCount <= 1 || useFbCollage) && "invisible"
                    )}
                    disabled={selectedItemIndex === mediaItems.length - 1}
                    onClick={() => onSelectItem(selectedItemIndex + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                {renditionPreview?.platform === "pinterest" &&
                  mediaItems.filter((item) => item.type === "image").length >
                    1 && (
                    <p className="max-w-md text-center text-xs text-muted-foreground">
                      Pinterest carousels publish 2-5 images. Posts with 6+
                      images publish the first image as a single Pin.
                    </p>
                  )}
              </div>
            ) : (
            <>
            {/* Normal editor mode */}
            <div className="w-full flex items-center justify-center gap-2">
              {mediaItems.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 rounded-full"
                  disabled={selectedItemIndex === 0}
                  onClick={() => onSelectItem(selectedItemIndex - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              <div className="media-content relative shadow-lg ring-1 ring-black/10 rounded-sm" style={previewStyle}>
                <ScenePreviewCanvasWrapper
                  backgroundImagePath={mediaItem?.path}
                  backgroundImageUrl={
                    selectedItemPreviewUrl || backgroundPreviewUrl || undefined
                  }
                  backgroundFilter={mediaItem?.metadata?.backgroundFilter}
                  overlays={initialOverlays}
                  overlayPreviewUrls={overlayPreviewUrls}
                  onOverlayChange={handleCanvasOverlayUpdate}
                  onInteractionStart={handleInteractionStart}
                  onSelectOverlay={(id) => {
                    setSelectedOverlayId(id);
                    console.log(
                      "[ImageCompositeEditor] setSelectedOverlayId invoked",
                      {
                        source: "canvas-selection",
                        value: id,
                      }
                    );
                  }}
                  selectedOverlayId={selectedOverlayId}
                  referenceMediaWidth={naturalImageDimensions?.width}
                  onEditorModeChange={setEditorMode}
                  onBackgroundImageResolved={onBackgroundImageResolved}
                  onDeleteOverlay={handleRemoveOverlay}
                  onDuplicateOverlay={handleCopyOverlay}
                  onAddOverlay={handleAddOverlay}
                />
              </div>
              {mediaItems.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 rounded-full"
                  disabled={selectedItemIndex === mediaItems.length - 1}
                  onClick={() => onSelectItem(selectedItemIndex + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
            {/* Undo/Redo buttons */}
            {(undo || redo) && (
              <div className="flex items-center justify-center gap-1 mt-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!canUndo}
                  onClick={undo}
                  title="Undo (Cmd+Z)"
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!canRedo}
                  onClick={redo}
                  title="Redo (Cmd+Shift+Z)"
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </div>
            )}
            </>
            )}
            </div>
          </div>
          {/* Filmstrip — hidden in rendition preview, where slide editing
              is out of scope and clicks would be inert */}
          {!inRenditionPreview && (
            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
            <div onClick={handleFilmstripClickInVideoMode}>
              {bottomSlot}
            </div>
          )}
        </div>

        <div
          className={cn(
            "w-full flex flex-col lg:w-80 lg:min-w-80 lg:border-l lg:pl-6",
            // Drill-down: no side column at all — the preview owns the full
            // width, and video controls live in a strip under the player
            inRenditionPreview && "hidden"
          )}
        >
          {libraryMode ? (
            <ImageLibraryPanel
              onBack={handleLibraryClose}
              onSelectPexelsImage={handlePexelsImageSelect}
              onSelectArticleImage={handleArticleImageSelect}
              onSelectBrandKitImage={handleBrandKitImageSelect}
              articleImages={extractedArticleImages || []}
              brandKits={userBrandKits}
              defaultTab={undefined}
            />
          ) : shapeLibraryMode ? (
            <ShapeLibraryView
              onBack={() => setShapeLibraryMode(false)}
              onSelect={handleShapeSelect}
            />
          ) : showVideoStage && !videoEditingSlide ? (
            /* Video settings panel — video posts and rendition previews */
            <div className="overflow-y-auto pr-2 pl-1 -ml-1 flex-1 animate-in fade-in duration-300 motion-reduce:animate-none">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Video Settings</h3>

                {isLoadingChoreography || isLoadingRendition ? (
                  <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-sm">Preparing video…</span>
                  </div>
                ) : (
                <VideoAudioSettings
                  postId={draftId}
                  persist={!inRenditionPreview}
                  onPersistSettings={onPersistVideoSettings}
                  disabled={
                    videoSettingsDisabled ||
                    isLoadingChoreography ||
                    isRevertingToCarousel
                  }
                  musicPath={effectiveMusicPath}
                  audioMuted={effectiveAudioMuted}
                  speed={effectiveSpeed}
                  onMusicChange={(path, name) => {
                    // Drill-down changes are channel-scoped overrides; the
                    // main editor edits the post's defaults
                    if (inRenditionPreview) {
                      renditionPreview?.onVideoOverridesChange?.({
                        videoMusicPath: path,
                        videoMusicName: name,
                      });
                    } else {
                      setVideoMusicPath(path);
                    }
                  }}
                  onMuteChange={(muted) => {
                    if (inRenditionPreview) {
                      renditionPreview?.onVideoOverridesChange?.({
                        videoAudioMuted: muted,
                      });
                    } else {
                      setVideoAudioMuted(muted);
                    }
                  }}
                  onSpeedChange={(s) => {
                    if (inRenditionPreview) {
                      renditionPreview?.onVideoOverridesChange?.({
                        videoSpeed: s,
                      });
                    } else {
                      setVideoSpeed(s);
                    }
                  }}
                />
                )}
              </div>
            </div>
          ) : isBackgroundSelected ? (
            <div className="overflow-y-auto pr-2 pl-1 -ml-1 flex-1">
              <div className="flex items-center mb-4">
                <button
                  onClick={() => setIsBackgroundSelected(false)}
                  className="mr-2 p-1 hover:bg-gray-100 rounded-md transition-colors"
                  aria-label="Back to image editor"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-arrow-left"
                  >
                    <path d="m12 19-7-7 7-7" />
                    <path d="M19 12H5" />
                  </svg>
                </button>
                <h3 className="text-lg font-medium">Background</h3>
              </div>
              <BackgroundFormatControls
                previewUrl={selectedItemPreviewUrl || backgroundPreviewUrl || null}
                hasImage={!!mediaItem?.path}
                loading={isLoadingBackgroundPreview || isUploadingImage || isUploadingBackground}
                initialBackgroundColor={initialBackgroundColor}
                onUpload={() => bgFileInputRef.current?.click()}
                onBrowseLibrary={() => setLibraryMode("background")}
                onChangeColor={handleFillColorChange}
                filter={backgroundFilter}
                onFilterChange={
                  onBackgroundFilterChange ? handleBackgroundFilterChange : undefined
                }
                isTemplateAsset={isBackgroundTemplateAsset}
                onTemplateAssetChange={onBackgroundTemplateAssetChange}
                brandColors={activeBrandColors}
                brandKitContext={brandKitColorPickerContext}
              />
              <Input
                type="file"
                accept="image/*"
                onChange={handleBackgroundImageUpload}
                ref={bgFileInputRef}
                disabled={isUploadingBackground}
                className="hidden"
              />
            </div>
          ) : selectedOverlay ? (
            <div className="overflow-y-auto pr-2 pl-1 -ml-1 flex-1">
              <div className="flex items-center mb-4">
                <button
                  aria-label="Back"
                  onClick={() => {
                    // A drilled-in panel (e.g. Edit Highlight) pops its own
                    // level first; only the top level deselects the overlay
                    if (overlayDrillDown) {
                      overlayDrillDown.onBack();
                      return;
                    }
                    setSelectedOverlayId(null);
                    console.log(
                      "[ImageCompositeEditor] setSelectedOverlayId invoked",
                      {
                        source: "back-button",
                        value: null,
                      }
                    );
                  }}
                  className="mr-2 p-1 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-arrow-left"
                  >
                    <path d="m12 19-7-7 7-7" />
                    <path d="M19 12H5" />
                  </svg>
                </button>
                <h3 className="text-lg font-medium">
                  {overlayDrillDown?.title ?? "Edit Overlay"}
                </h3>
              </div>
              {isTextOverlay(selectedOverlay) && (
                <TextOverlayFormatControls
                  textOverlay={selectedOverlay}
                  onChange={handleFormatChange}
                  onDelete={handleRemoveOverlay}
                  onDuplicate={handleCopyOverlay}
                  editorMode={editorMode}
                  brandColors={activeBrandColors}
                  brandKitContext={brandKitColorPickerContext}
                  onDrillDownChange={setOverlayDrillDown}
                />
              )}
              {isImageOverlay(selectedOverlay) && (
                <ImageOverlayFormatControls
                  imageOverlay={selectedOverlay}
                  onChange={handleFormatChange}
                  onDelete={handleRemoveOverlay}
                  onDuplicate={handleCopyOverlay}
                  onChangeImageUpload={handleChangeOverlayImageUpload}
                  onBrowseLibrary={handleChangeOverlayBrowseLibrary}
                  previewUrl={selectedOverlayPreviewUrl}
                  loading={isUploadingImage}
                  onFillColorChange={handleOverlayFillColorChange}
                  brandColors={activeBrandColors}
                  brandKitContext={brandKitColorPickerContext}
                />
              )}
              {isSvgOverlay(selectedOverlay) && (
                <SvgOverlayFormatControls
                  svgOverlay={selectedOverlay}
                  onChange={handleFormatChange}
                  onDelete={handleRemoveOverlay}
                  onDuplicate={handleCopyOverlay}
                  brandColors={activeBrandColors}
                  brandKitContext={brandKitColorPickerContext}
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="mb-4 flex items-center gap-1">
                <h3 className="text-lg font-medium">Image Editor</h3>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs text-muted-foreground" title="Keyboard shortcuts">
                      <Keyboard className="h-3.5 w-3.5" />
                      Shortcuts
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent side="bottom" align="end" className="w-56 p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Keyboard Shortcuts</p>
                    <div className="grid gap-1.5 text-xs">
                      <div className="flex justify-between"><span>Undo</span><kbd className="text-muted-foreground">⌘Z</kbd></div>
                      <div className="flex justify-between"><span>Redo</span><kbd className="text-muted-foreground">⌘⇧Z</kbd></div>
                      <div className="flex justify-between"><span>Copy overlay</span><kbd className="text-muted-foreground">⌘C</kbd></div>
                      <div className="flex justify-between"><span>Paste overlay</span><kbd className="text-muted-foreground">⌘V</kbd></div>
                      <div className="flex justify-between"><span>Delete overlay</span><kbd className="text-muted-foreground">⌫</kbd></div>
                      <div className="flex justify-between"><span>Nudge</span><kbd className="text-muted-foreground">Arrow keys</kbd></div>
                      <div className="flex justify-between"><span>Nudge 10px</span><kbd className="text-muted-foreground">⇧ + Arrow</kbd></div>
                      <div className="flex justify-between"><span>Deselect</span><kbd className="text-muted-foreground">Esc</kbd></div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Background Image</Label>
                  {/* Preview thumbnail — click to open Background edit panel */}
                  {(mediaItem?.path || isLoadingBackgroundPreview || isUploadingImage) && (
                    <div className="mt-2 mb-2">
                      {isLoadingBackgroundPreview || isUploadingImage ? (
                        <div className="w-full h-48 bg-muted rounded-md flex items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      ) : backgroundPreviewUrl ? (
                        <button
                          type="button"
                          onClick={() => setIsBackgroundSelected(true)}
                          aria-label="Edit background"
                          className="group relative w-full h-48 rounded-md bg-slate-100 overflow-hidden ring-1 ring-transparent hover:ring-foreground/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition"
                        >
                          <img
                            src={backgroundPreviewUrl}
                            alt="Background preview"
                            className="h-full w-full object-contain"
                          />
                          <span className="pointer-events-none absolute bottom-2 right-2 rounded-[4px] bg-foreground/85 text-background text-[10.5px] uppercase tracking-[0.08em] px-1.5 py-0.5 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
                            Edit
                          </span>
                        </button>
                      ) : null}
                    </div>
                  )}
                  <div className="flex gap-2 mt-1">
                    <ImageSourceButton
                      label={mediaItem?.path ? "Change" : "Upload"}
                      onUpload={() => bgFileInputRef.current?.click()}
                      onBrowseLibrary={() => setLibraryMode("background")}
                      loading={isUploadingBackground}
                      className="flex-1"
                      onChangeColor={handleFillColorChange}
                      currentColor={initialBackgroundColor || "#000000"}
                      brandColors={activeBrandColors}
                      brandKitContext={brandKitColorPickerContext}
                    />
                  </div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleBackgroundImageUpload}
                    ref={bgFileInputRef}
                    disabled={isUploadingBackground}
                    className="hidden"
                  />
                </div>
                <Separator />
                <div className="space-y-2">
                  <div>
                    <Label>Overlays</Label>
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleAddTextOverlay}
                        className="flex-1"
                      >
                        <PlusCircle className="mr-2 h-4 w-4" />
                        Text
                      </Button>
                      <ImageSourceButton
                        label="Image"
                        onUpload={() => overlayImageInputRef.current?.click()}
                        onBrowseLibrary={() => setLibraryMode("overlay")}
                        loading={isUploadingOverlay}
                        showPlusIcon
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShapeLibraryMode(true)}
                        className="flex-1"
                      >
                        <PlusCircle className="mr-2 h-4 w-4" />
                        Shape
                      </Button>
                    </div>
                    <Input
                      ref={overlayImageInputRef}
                      type="file"
                      accept="image/*,.svg"
                      onChange={handleImageOverlayUpload}
                      className="hidden"
                    />
                  </div>
                </div>
                {initialOverlays.length > 0 && <Separator className="my-4" />}
              </div>
              {/* Scrollable overlay list */}
              {initialOverlays.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={displayOverlays.map((o) => o.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-2 mt-2">
                      {displayOverlays.map((overlay) => (
                        <OverlayListItem
                          key={overlay.id}
                          overlay={overlay}
                          isSelected={selectedOverlayId === overlay.id}
                          onSelect={() => {
                            setSelectedOverlayId(overlay.id);
                            console.log(
                              "[ImageCompositeEditor] setSelectedOverlayId invoked",
                              {
                                source: "overlay-list",
                                value: overlay.id,
                              }
                            );
                          }}
                          onRemove={() => handleRemoveOverlay(overlay.id)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Hidden file input for changing overlay images - rendered outside conditional so ref is always available */}
      <Input
        ref={changeOverlayImageInputRef}
        type="file"
        accept="image/*"
        onChange={handleChangeOverlayImageUploadFile}
        className="hidden"
        style={{ position: "absolute", left: "-9999px" }}
      />
      {/* Hidden file input for changing SVG overlays */}
      <Input
        ref={changeSvgInputRef}
        type="file"
        accept=".svg,image/svg+xml"
        onChange={handleChangeSvgUploadFile}
        className="hidden"
        style={{ position: "absolute", left: "-9999px" }}
      />


    </div>
  );
}

interface OverlayListItemProps {
  overlay: AnyOverlay;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}

const OverlayListItem = ({
  overlay,
  isSelected,
  onSelect,
  onRemove,
}: OverlayListItemProps) => {
  const { toast } = useToast();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: overlay.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => {
    let isMounted = true;
    const fetchPreview = async () => {
      // SVG overlays now have svgContent directly - no need to fetch from storage
      if (isSvgOverlay(overlay)) {
        setIsLoading(false);
        return;
      }
      
      if (isImageOverlay(overlay) && overlay.filePath) {
        setIsLoading(true);
        try {
          const storage = getStorage();
          const storageRef = ref(storage, overlay.filePath);
          const url = await getSignedUrl(storageRef.fullPath);
          if (isMounted) setPreviewUrl(url);
        } catch (error) {
          console.error("Error fetching overlay preview URL:", error);
        } finally {
          if (isMounted) setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    fetchPreview();
    return () => {
      isMounted = false;
    };
  }, [overlay, toast]);

  const name = isTextOverlay(overlay)
    ? overlay.text || "Empty Text"
    : isSvgOverlay(overlay)
      ? "Shape"
      : isImageOverlay(overlay) && overlay.filePath
        ? overlay.filePath.split("/").pop()?.split("-").slice(1).join("-") || "Image"
        : "Image";
  
  const typeLabel = overlay.type === "svg" ? "Shape" : overlay.type === "image" ? "Image" : "Text";

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`flex items-center space-x-2 p-2 rounded-md cursor-pointer border ${
        isSelected
          ? "border-primary bg-primary/10"
          : "border-transparent hover:bg-muted/50"
      }`}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {isLoading ? (
        <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : isSvgOverlay(overlay) && overlay.svgContent ? (
        // Render SVG shape inline using svgContent
        <div
          className="w-10 h-10 bg-muted rounded-md flex items-center justify-center p-1.5"
          dangerouslySetInnerHTML={{
            __html: `<svg viewBox="${overlay.viewBox || '0 0 100 100'}" class="w-full h-full" style="color: ${overlay.fillColor || '#374151'};">${namespaceSvgIds(overlay.svgContent, `list-${overlay.id}`)}</svg>`,
          }}
        />
      ) : previewUrl ? (
        <img
          src={previewUrl}
          alt="Overlay preview"
          className="w-10 h-10 rounded-md object-cover bg-slate-200"
        />
      ) : (
        <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center text-muted-foreground">
          {isTextOverlay(overlay) ? (
            <span className="font-bold text-lg select-none">T</span>
          ) : isSvgOverlay(overlay) ? (
            <span className="font-bold text-xs select-none">SVG</span>
          ) : (
            <div />
          )}
        </div>
      )}
      <div className="flex-1 min-w-0 text-sm">
        <p className="font-medium truncate">{name}</p>
        <p className="text-xs text-muted-foreground">
          {typeLabel}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="h-8 w-8"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};
